/**
 * Parser.js
 *
 * A parser for rational interval arithmetic expressions.
 * Handles expressions with intervals, arithmetic operations, and parentheses.
 * Supports decimal uncertainty notation including range [56:67], relative [+5:-6], and symmetric [+-1] formats.
 */

import {
  Integer,
  Rational,
  RationalInterval,
  BaseSystem,
  Fraction,
  FractionInterval,
} from "@ratmath/core";

import {
  newtonRoot,
  rationalIntervalPower,
} from "@ratmath/reals";

export { BaseParser } from "./base-parser.js";

const DEFAULT_PRECISION = -6; // 10^-6

/**
 * Parses a decimal with uncertainty notation and returns a RationalInterval
 * Supports formats like:
 * - 1.23[56:67] → 1.2356:1.2367 (range notation)
 * - 1.23[+5:-6] → 1.17:1.28 (relative notation)
 * - 1.3[+-1] → 1.2:1.4 (symmetric notation)
 *
 * @param {string} str - String with uncertainty notation
 * @returns {RationalInterval} The interval representation
 * @throws {Error} If the string format is invalid
 */
function parseDecimalUncertainty(str, options = {}) {
  const allowIntegerRangeNotation = options.allowIntegerRangeNotation !== false;
  const inputBase = options.inputBase || BaseSystem.DECIMAL;

  const uncertaintyMatch = str.match(/^(-?[@\w./:^]+)\[([^\]]+)\]((?:[Ee][+-]?[\w]+|\_?\^-?[\w]+)?)$/);
  if (!uncertaintyMatch) {
    throw new Error("Invalid uncertainty format");
  }

  const baseStr = uncertaintyMatch[1];
  const uncertaintyStr = uncertaintyMatch[2];
  const trailingPart = uncertaintyMatch[3];

  if (uncertaintyStr.includes(",")) {
    throw new Error(
      "Decimal interval notation requires ':' between bracketed values",
    );
  }

  // Helper to apply trailing scientific notation
  const finalize = (interval) => {
    if (trailingPart) {
      const multiplier = parseRepeatingDecimalOrRegular("1" + trailingPart, inputBase);
      return interval.multiply(multiplier);
    }
    return interval;
  };

  // Check if this is a range interval right after decimal point
  // Format: 0.[#3:#6] or 1.[1:4] (only when base ends with decimal point and no digits after)
  const afterDecimalMatch = baseStr.match(/^(-?[\w./:^]+\.)$/);
  if (
    afterDecimalMatch &&
    !uncertaintyStr.includes("+") &&
    !uncertaintyStr.includes("-")
  ) {
    return finalize(parseDecimalPointUncertainty(baseStr, uncertaintyStr, inputBase, options));
  }

  // Parse the base value
  const baseValue = parseBaseNotation(baseStr, inputBase, { ...options, typeAware: true });

  // Determine decimal places in base for proper alignment
  const decimalMatch = baseStr.match(/\.([\w^/_]+)$/);
  const baseDecimalPlaces = decimalMatch ? decimalMatch[1].length : 0;

  let result;

  // Check if it's range notation [num:num], relative notation [+num:-num], or symmetric notation [+-num]
  if (
    uncertaintyStr.includes(":") &&
    !uncertaintyStr.includes("+") &&
    !uncertaintyStr.includes("-")
  ) {
    // Range notation: 1.23[56:67] → 1.2356:1.2367
    if (baseDecimalPlaces === 0 && !allowIntegerRangeNotation) {
      throw new Error(
        "Range notation on integer bases is not supported in this context",
      );
    }

    const rangeParts = uncertaintyStr.split(/\s*:\s*/);
    if (
      rangeParts.length !== 2 ||
      rangeParts.some((part) => part.length === 0)
    ) {
      throw new Error(
        "Range notation must have exactly two values separated by colon",
      );
    }

    const lowerUncertainty = rangeParts[0].trim();
    const upperUncertainty = rangeParts[1].trim();

    // Reject E notation in the base string if it's confusing (scientific notation)
    const hasConfusingENotation = (inputBase.base === 10 && (baseStr.includes("E") || baseStr.includes("e"))) || baseStr.includes("_^");
    if (hasConfusingENotation) {
      throw new Error("Uncertainty notation cannot be used with scientific notation in the base value");
    }

    const isValidForBase = (s) => {
      if (inputBase.isValidString(s.replace(".", ""))) return true;
      if (inputBase.base <= 36) {
        return inputBase.isValidString(s.replace(".", "").toLowerCase());
      }
      return false;
    };

    if (!isValidForBase(lowerUncertainty) || !isValidForBase(upperUncertainty)) {
      throw new Error(`Range values must be valid for base ${inputBase.base}`);
    }

    const lowerBoundStr = baseStr + lowerUncertainty;
    const upperBoundStr = baseStr + upperUncertainty;

    const lowerBoundResult = parseBaseNotation(lowerBoundStr, inputBase, { ...options, typeAware: true });
    const upperBoundResult = parseBaseNotation(upperBoundStr, inputBase, { ...options, typeAware: true });

    let lowerBound = lowerBoundResult instanceof Integer ? lowerBoundResult.toRational() : lowerBoundResult;
    let upperBound = upperBoundResult instanceof Integer ? upperBoundResult.toRational() : upperBoundResult;

    result = lowerBound.greaterThan(upperBound)
      ? new RationalInterval(upperBound, lowerBound)
      : new RationalInterval(lowerBound, upperBound);

  } else if (
    uncertaintyStr.startsWith("+-") ||
    uncertaintyStr.startsWith("-+")
  ) {
    // Symmetric notation: 1.23[+-5]
    const offsetStr = uncertaintyStr.substring(2);
    if (!offsetStr) {
      throw new Error(
        "Symmetric notation must have a valid number after +- or -+",
      );
    }

    const offset = parseRepeatingDecimalOrRegular(offsetStr, inputBase);
    const baseVal = BigInt(inputBase.base);

    if (baseDecimalPlaces === 0 && !baseStr.includes(".")) {
      const upperBound = baseValue.add(offset);
      const lowerBound = baseValue.subtract(offset);
      result = new RationalInterval(lowerBound, upperBound);
    } else {
      const lastPlaceScale = new Rational(1).divide(
        new Rational(baseVal).pow(baseDecimalPlaces),
      );
      const scaledOffset = offset.multiply(lastPlaceScale);
      const upperBound = baseValue.add(scaledOffset);
      const lowerBound = baseValue.subtract(scaledOffset);
      result = new RationalInterval(lowerBound, upperBound);
    }
  } else {
    // Relative notation: 1.23[+5:-6]
    const relativeParts = uncertaintyStr.split(/\s*:\s*/).map((s) => s.trim());
    if (relativeParts.length > 2 || relativeParts.length === 0) {
      throw new Error(
        "Relative notation must have one or two values separated by colon",
      );
    }

    const hasConfusingENotation = (inputBase.base === 10 && (baseStr.includes("E") || baseStr.includes("e"))) || baseStr.includes("_^");
    if (hasConfusingENotation) {
      throw new Error("Uncertainty notation cannot be used with scientific notation in the base value");
    }

    let positiveOffset = null;
    let negativeOffset = null;

    for (const part of relativeParts) {
      if (part.startsWith("+")) {
        if (positiveOffset !== null) throw new Error("Only one positive offset allowed");
        const offsetStr = part.substring(1);
        if (!offsetStr) throw new Error("Offset must be a valid number");
        positiveOffset = parseRepeatingDecimalOrRegular(offsetStr, inputBase);
      } else if (part.startsWith("-")) {
        if (negativeOffset !== null) throw new Error("Only one negative offset allowed");
        const offsetStr = part.substring(1);
        if (!offsetStr) throw new Error("Offset must be a valid number");
        negativeOffset = parseRepeatingDecimalOrRegular(offsetStr, inputBase);
      } else {
        throw new Error("Relative notation values must start with + or -");
      }
    }

    if (positiveOffset === null) positiveOffset = new Integer(0);
    if (negativeOffset === null) negativeOffset = new Integer(0);

    const baseVal = BigInt(inputBase.base);

    let upperBound, lowerBound;
    if (baseDecimalPlaces === 0 && !baseStr.includes(".")) {
      upperBound = baseValue.add(positiveOffset);
      lowerBound = baseValue.subtract(negativeOffset);
    } else {
      const lastPlaceScale = new Rational(1).divide(
        new Rational(baseVal).pow(baseDecimalPlaces),
      );

      const scaledPositiveOffset = positiveOffset.multiply(lastPlaceScale);
      const scaledNegativeOffset = negativeOffset.multiply(lastPlaceScale);
      upperBound = baseValue.add(scaledPositiveOffset);
      lowerBound = baseValue.subtract(scaledNegativeOffset);
    }

    result = new RationalInterval(lowerBound, upperBound);
  }

  return finalize(result);
}

function parseDecimalPointUncertainty(baseStr, uncertaintyStr, baseSystem = BaseSystem.DECIMAL, options = {}) {
  // Handle range notation right after decimal point
  // baseStr is like "0." or "-1."

  if (uncertaintyStr.includes(":")) {
    // Range notation: 0.[#3:#6] or 0.[1:4]
    const rangeParts = uncertaintyStr.split(/\s*:\s*/);
    if (
      rangeParts.length !== 2 ||
      rangeParts.some((part) => part.length === 0)
    ) {
      throw new Error(
        "Range notation must have exactly two values separated by colon",
      );
    }

    const lowerStr = rangeParts[0].trim();
    const upperStr = rangeParts[1].trim();

    // Parse each endpoint, handling repeating decimals
    const lowerBound = parseDecimalPointEndpoint(baseStr, lowerStr, baseSystem, options);
    const upperBound = parseDecimalPointEndpoint(baseStr, upperStr, baseSystem, options);

    return new RationalInterval(lowerBound, upperBound);
  } else {
    throw new Error("Invalid uncertainty format for decimal point notation");
  }
}

function parseDecimalPointEndpoint(baseStr, endpointStr, baseSystem = BaseSystem.DECIMAL, options = {}) {
  // baseStr is like "0." or "-1."
  // endpointStr is like "#3", "1", "4", etc.

  if (endpointStr.startsWith("#")) {
    // Repeating decimal: combine base with repeating part
    // Note: Repeating decimals with # are currently only supported in Base 10
    const fullStr = baseStr + endpointStr;
    return parseRepeatingDecimal(fullStr);
  } else {
    // Simple digits: append to base
    const fullStr = baseStr + endpointStr;
    try {
      const result = parseBaseNotation(fullStr, baseSystem, { ...options, typeAware: true });
      return result instanceof Integer ? result.toRational() : result;
    } catch (e) {
      throw new Error(`Invalid endpoint format: ${endpointStr}`);
    }
  }
}

function parseRepeatingDecimalOrRegular(str, baseSystem = BaseSystem.DECIMAL) {
  // Parse a string that might be a repeating decimal, regular decimal, or E/_^ notation
  if (str.includes("#")) {
    // Check for E notation or _^ notation after the repeating decimal
    let eNotationIndex = -1;
    let eNotationType = null;

    const explicitSciIndex = str.indexOf("_^");
    if (explicitSciIndex !== -1) {
      eNotationIndex = explicitSciIndex;
      eNotationType = "_^";
    } else if (baseSystem.base === 10) {
      const eIndex = str.toUpperCase().indexOf("E");
      if (eIndex !== -1) {
        eNotationIndex = eIndex;
        eNotationType = "E";
      }
    }

    if (eNotationIndex !== -1) {
      const repeatingPart = str.substring(0, eNotationIndex);
      const exponentPart = str.substring(eNotationIndex + (eNotationType === "_^" ? 2 : 1));

      // Validate exponent is a valid integer in current base
      const absExponentPart = exponentPart.startsWith("-") ? exponentPart.substring(1) : exponentPart;
      if (!baseSystem.isValidString(absExponentPart)) {
        throw new Error(`${eNotationType} notation exponent must be a valid integer in base ${baseSystem.base}`);
      }

      const baseValue = parseRepeatingDecimal(repeatingPart);
      const exponent = baseSystem.toDecimal(exponentPart);

      // Apply notation: multiply by scaleBase^exponent
      // E always uses base 10, _^ uses the current base system
      const scaleBaseNum = eNotationType === "E" ? 10 : baseSystem.base;
      const scaleBaseRatio = new Rational(BigInt(scaleBaseNum));
      let scale;
      if (exponent >= 0n) {
        scale = scaleBaseRatio.pow(exponent);
      } else {
        scale = new Rational(1).divide(scaleBaseRatio.pow(-exponent));
      }

      return baseValue.multiply(scale);
    } else {
      return parseRepeatingDecimal(str);
    }
  } else {
    // Regular decimal or E/_^ notation (handled by parseBaseNotation)
    return parseBaseNotation(str, baseSystem);
  }
}

/**
 * Parses a repeating decimal string and returns the exact rational equivalent
 *
 * @param {string} str - String like "0.12#45" or "733.#3" or "1.23#0" or "0.#3:0.5#0"
 * @returns {Rational|RationalInterval} The exact rational representation, or interval for non-repeating decimals
 * @throws {Error} If the string format is invalid
 */
export function parseRepeatingDecimal(str) {
  if (!str || typeof str !== "string") {
    throw new Error("Input must be a non-empty string");
  }

  str = str.trim();

  // Check if this is uncertainty notation (contains brackets)
  if (str.includes("[") && str.includes("]")) {
    return parseDecimalUncertainty(str, { allowIntegerRangeNotation: false }); // Don't allow integer range notation in parseRepeatingDecimal
  }

  // Check if this is an interval notation (contains colon)
  if (str.includes(":")) {
    return parseRepeatingDecimalInterval(str);
  }

  // Handle negative numbers
  const isNegative = str.startsWith("-");
  if (isNegative) {
    str = str.substring(1);
  }

  // Check if this is a non-repeating decimal (no # symbol)
  if (!str.includes("#")) {
    return parseNonRepeatingDecimal(str, isNegative);
  }

  // Split on the # symbol
  const parts = str.split("#");
  if (parts.length !== 2) {
    throw new Error(
      'Invalid repeating decimal format. Use format like "0.12#45"',
    );
  }

  const [nonRepeatingPart, repeatingPart] = parts;

  // Validate repeating part
  if (!/^\d+$/.test(repeatingPart)) {
    throw new Error("Repeating part must contain only digits");
  }

  // Handle special case where repeating part is "0" - this means the decimal terminates
  if (repeatingPart === "0") {
    try {
      // Convert decimal string to rational manually
      const decimalParts = nonRepeatingPart.split(".");
      if (decimalParts.length > 2) {
        throw new Error("Invalid decimal format - multiple decimal points");
      }

      const integerPart = decimalParts[0] || "0";
      const fractionalPart = decimalParts[1] || "";

      if (!/^\d*$/.test(integerPart) || !/^\d*$/.test(fractionalPart)) {
        throw new Error(
          "Decimal must contain only digits and at most one decimal point",
        );
      }

      let numerator, denominator;
      if (!fractionalPart) {
        // Just an integer
        numerator = BigInt(integerPart);
        denominator = 1n;
      } else {
        // Convert decimal to fraction
        numerator = BigInt(integerPart + fractionalPart);
        denominator = 10n ** BigInt(fractionalPart.length);
      }

      const rational = new Rational(numerator, denominator);
      return isNegative ? rational.negate() : rational;
    } catch (error) {
      throw new Error(`Invalid decimal format: ${error.message}`);
    }
  }

  // Split non-repeating part into integer and fractional parts
  const decimalParts = nonRepeatingPart.split(".");
  if (decimalParts.length > 2) {
    throw new Error("Invalid decimal format - multiple decimal points");
  }

  const integerPart = decimalParts[0] || "0";
  const fractionalPart = decimalParts[1] || "";

  // Validate parts contain only digits
  if (!/^\d*$/.test(integerPart) || !/^\d*$/.test(fractionalPart)) {
    throw new Error(
      "Non-repeating part must contain only digits and at most one decimal point",
    );
  }

  // Calculate the rational representation
  const n = fractionalPart.length; // number of non-repeating fractional digits
  const m = repeatingPart.length; // number of repeating digits

  // Create the numbers: abc (concatenated) and ab (non-repeating part only)
  const abcStr = integerPart + fractionalPart + repeatingPart;
  const abStr = integerPart + fractionalPart;

  const abc = BigInt(abcStr);
  const ab = BigInt(abStr);

  // Calculate denominator: 10^(n+m) - 10^n = 10^n * (10^m - 1)
  const powerOfTenN = 10n ** BigInt(n);
  const powerOfTenM = 10n ** BigInt(m);
  const denominator = powerOfTenN * (powerOfTenM - 1n);

  // Calculate numerator: abc - ab
  const numerator = abc - ab;

  let result = new Rational(numerator, denominator);
  return isNegative ? result.negate() : result;
}

/**
 * Parses a non-repeating decimal and returns an interval representing the uncertainty
 * For example, "1.23" becomes the interval [1.225, 1.235)
 *
 * @private
 * @param {string} str - Decimal string like "1.23"
 * @param {boolean} isNegative - Whether the number is negative
 * @returns {RationalInterval} The interval representation
 */
function parseNonRepeatingDecimal(str, isNegative) {
  // Validate decimal format
  const decimalParts = str.split(".");
  if (decimalParts.length > 2) {
    throw new Error("Invalid decimal format - multiple decimal points");
  }

  const integerPart = decimalParts[0] || "0";
  const fractionalPart = decimalParts[1] || "";

  if (!/^\d+$/.test(integerPart) || !/^\d*$/.test(fractionalPart)) {
    throw new Error(
      "Decimal must contain only digits and at most one decimal point",
    );
  }

  // If there's no fractional part, treat as exact integer
  if (!fractionalPart) {
    const rational = new Rational(integerPart);
    return isNegative ? rational.negate() : rational;
  }

  // Create interval [x.yyy5, x.yyy5) where the last digit is treated as ±0.5
  const lastDigitPlace = 10n ** BigInt(fractionalPart.length + 1);
  const baseValue = BigInt(integerPart + fractionalPart);

  let lower, upper;

  if (isNegative) {
    // For negative numbers like -1.5, we want [-1.55, -1.45]
    // So we need to add 5 and subtract 5 from baseValue * 10, then negate
    const lowerNumerator = -(baseValue * 10n + 5n);
    const upperNumerator = -(baseValue * 10n - 5n);

    lower = new Rational(lowerNumerator, lastDigitPlace);
    upper = new Rational(upperNumerator, lastDigitPlace);
  } else {
    // For positive numbers like 0.5, we want [0.45, 0.55]
    // baseValue = 5, lastDigitPlace = 100
    // lower = (5 * 10 - 5) / 100 = 45/100 = 9/20
    // upper = (5 * 10 + 5) / 100 = 55/100 = 11/20
    const lowerNumerator = baseValue * 10n - 5n;
    const upperNumerator = baseValue * 10n + 5n;

    lower = new Rational(lowerNumerator, lastDigitPlace);
    upper = new Rational(upperNumerator, lastDigitPlace);
  }

  return new RationalInterval(lower, upper);
}

/**
 * Parses a repeating decimal interval string like "0.#3:0.5#0"
 *
 * @private
 * @param {string} str - Interval string with colon separator
 * @returns {RationalInterval} The interval representation
 */
function parseRepeatingDecimalInterval(str) {
  const parts = str.split(":");
  if (parts.length !== 2) {
    throw new Error('Invalid interval format. Use format like "0.#3:0.5#0"');
  }

  // Parse each endpoint separately
  const leftEndpoint = parseRepeatingDecimal(parts[0].trim());
  const rightEndpoint = parseRepeatingDecimal(parts[1].trim());

  // If either endpoint is an interval, we need to handle that
  if (
    leftEndpoint instanceof RationalInterval ||
    rightEndpoint instanceof RationalInterval
  ) {
    throw new Error("Nested intervals are not supported");
  }

  // Create interval from the two rational endpoints
  return new RationalInterval(leftEndpoint, rightEndpoint);
}

/**
 * Parses a number string in a specific base system
 * Supports integers, decimals, mixed numbers, intervals, and E notation
 *
 * @param {string} numberStr - The number string to parse (e.g., "101", "10.1", "1..1/2", "A:F", "12E2", "12_^2")
 * @param {BaseSystem} baseSystem - The base system to use for parsing
 * @param {Object} options - Parsing options
 * @returns {Integer|Rational|RationalInterval} The parsed value
 */
function parseBaseNotation(numberStr, baseSystem, options = {}) {
  // Check for deprecated bracket notation first and throw error
  if (/\[[0-9a-zA-Z]+\]$/.test(numberStr) && !numberStr.startsWith("0z[")) {
    throw new Error(
      `Bracket base notation (Value[Base]) is no longer supported. Use prefix notation (0xValue, 0bValue) or the BASE command. Offending string: '${numberStr}'`
    );
  }

  // Handle negative numbers
  let isNegative = false;
  if (numberStr.startsWith("-")) {
    isNegative = true;
    numberStr = numberStr.substring(1);
  }

  // Check for custom base notation (0z[N])
  const customBaseMatch = numberStr.match(/^0z\[(\d+)\]/i);
  if (customBaseMatch) {
    const baseValue = parseInt(customBaseMatch[1], 10);
    try {
      baseSystem = BaseSystem.fromBase(baseValue);
    } catch (e) {
      throw new Error(`Invalid custom base '0z[${baseValue}]': ${e.message}`);
    }
    numberStr = numberStr.substring(customBaseMatch[0].length);
  } else {
    // Check for prefix notation (0x, 0b, etc.)
    // Regex: starts with 0 follow by a letter
    const prefixMatch = numberStr.match(/^0([a-zA-Z])/);
    if (prefixMatch) {
      const prefix = prefixMatch[1];
      const registeredBase = BaseSystem.getSystemForPrefix(prefix);
      // console.log(`DEBUG: prefix='${prefix}', registeredBase=${!!registeredBase}`);

      if (registeredBase) {
        // Switch base system and strip prefix
        baseSystem = registeredBase;
        numberStr = numberStr.substring(2); // Skip '0' and prefix char (e.g. '0x')
      } else if (prefix === "D") {
        // Special prefix for "default input base" - keep current baseSystem
        numberStr = numberStr.substring(2);
      } else {
        // If it looks like a prefix but isn't registered, throw error
        // Exception: 'E' is special for scientific notation
        if (prefix.toLowerCase() !== "e") {
          // We throw an explicit error with the parsed string to debug
          throw new Error(`Invalid or unregistered prefix '0${prefix}' for string '${numberStr}'`);
        }
      }
    }
  }

  // Check for base-aware E notation or _^ notation first
  let eNotationIndex = -1;
  let eNotationType = null;


  // Check for _^ notation (Always supported)
  const explicitSciIndex = numberStr.indexOf("_^");
  if (explicitSciIndex !== -1) {
    eNotationIndex = explicitSciIndex;
    eNotationType = "_^";
  } else {
    // Check for E notation (Only allowed in Base 10 per user request)
    if (baseSystem.base === 10) {
      // Look for E notation (case insensitive)
      const upperStr = numberStr.toUpperCase();
      const eIndex = upperStr.indexOf("E");
      if (eIndex !== -1) {
        eNotationIndex = eIndex;
        eNotationType = "E";
      }
    }
  }

  // If E notation found, split the number
  let baseNumber = numberStr;
  let exponentStr = null;

  if (eNotationIndex !== -1) {
    baseNumber = numberStr.substring(0, eNotationIndex);
    const exponentStart = eNotationIndex + (eNotationType === "_^" ? 2 : 1);
    exponentStr = numberStr.substring(exponentStart);

    // Validate exponent string contains only valid base characters
    if (!baseSystem.isValidString(exponentStr.replace("-", ""))) {
      throw new Error(
        `Invalid exponent "${exponentStr}" for base ${baseSystem.base}`,
      );
    }
  }

  // Normalize case for bases that use letters, but respect the base system's character case
  if (baseSystem.base <= 36 && baseSystem.base > 10) {
    // Check if the base system uses lowercase or uppercase for letters
    const usesLowercase = baseSystem.characters.some(
      (char) => char >= "a" && char <= "z",
    );
    const usesUppercase = baseSystem.characters.some(
      (char) => char >= "A" && char <= "Z",
    );

    if (usesLowercase && !usesUppercase) {
      // Base uses only lowercase letters
      baseNumber = baseNumber.toLowerCase();
      if (exponentStr) {
        exponentStr = exponentStr.toLowerCase();
      }
    } else if (usesUppercase && !usesLowercase) {
      // Base uses only uppercase letters
      baseNumber = baseNumber.toUpperCase();
      if (exponentStr) {
        exponentStr = exponentStr.toUpperCase();
      }
    }
    // If base uses mixed case or no letters, don't normalize
  }

  // Handle E notation if found
  if (eNotationIndex !== -1) {
    // Parse the base number (without exponent)
    const baseValue = parseBaseNotation(baseNumber, baseSystem, options);

    // Parse the exponent in the same base system
    let exponentDecimal;
    if (exponentStr.startsWith("-")) {
      const positiveExponent = baseSystem.toDecimal(exponentStr.substring(1));
      exponentDecimal = -positiveExponent;
    } else {
      exponentDecimal = baseSystem.toDecimal(exponentStr);
    }

    // Apply E notation: multiply by base^exponent
    let powerOfBase;
    const baseBigInt = BigInt(baseSystem.base);

    if (exponentDecimal >= 0n) {
      // Positive exponent: base^n
      powerOfBase = new Rational(baseBigInt ** exponentDecimal);
    } else {
      // Negative exponent: 1/(base^(-n))
      powerOfBase = new Rational(1n, baseBigInt ** -exponentDecimal);
    }

    // Convert baseValue to Rational for multiplication
    let baseRational;
    if (baseValue instanceof Integer) {
      baseRational = baseValue.toRational();
    } else if (baseValue instanceof Rational) {
      baseRational = baseValue;
    } else {
      throw new Error(
        "E notation can only be applied to simple numbers, not intervals",
      );
    }

    let result = baseRational.multiply(powerOfBase);
    if (isNegative) {
      result = result.negate();
    }

    return options.typeAware && result.denominator === 1n
      ? new Integer(result.numerator)
      : result;
  }

  // Check for interval notation first (contains colon)
  if (baseNumber.includes(":")) {
    const parts = baseNumber.split(":");
    if (parts.length !== 2) {
      throw new Error(
        'Base notation intervals must have exactly two endpoints separated by ":"',
      );
    }

    // For intervals, handle the negative sign by applying it to the left endpoint
    const leftStr = isNegative ? "-" + parts[0].trim() : parts[0].trim();
    const leftValue = parseBaseNotation(leftStr, baseSystem, options);
    const rightValue = parseBaseNotation(parts[1].trim(), baseSystem, options);

    // Convert to rationals for interval creation
    let leftRational, rightRational;
    if (leftValue instanceof Integer) {
      leftRational = leftValue.toRational();
    } else if (leftValue instanceof Rational) {
      leftRational = leftValue;
    } else if (
      leftValue instanceof RationalInterval &&
      leftValue.low.equals(leftValue.high)
    ) {
      leftRational = leftValue.low;
    } else {
      throw new Error(
        "Interval endpoints must be single values, not intervals",
      );
    }

    if (rightValue instanceof Integer) {
      rightRational = rightValue.toRational();
    } else if (rightValue instanceof Rational) {
      rightRational = rightValue;
    } else if (
      rightValue instanceof RationalInterval &&
      rightValue.low.equals(rightValue.high)
    ) {
      rightRational = rightValue.low;
    } else {
      throw new Error(
        "Interval endpoints must be single values, not intervals",
      );
    }

    const interval = new RationalInterval(leftRational, rightRational);
    interval._explicitInterval = true;
    return interval;
  }

  // Check for mixed number notation (double dot)
  if (baseNumber.includes("..")) {
    const parts = baseNumber.split("..");
    if (parts.length !== 2) {
      throw new Error(
        'Mixed number notation must have exactly one ".." separator',
      );
    }

    const wholePart = parts[0].trim();
    const fractionPart = parts[1].trim();

    if (!fractionPart.includes("/")) {
      throw new Error('Mixed number fractional part must contain "/"');
    }

    // Parse whole part in the specified base
    const wholeDecimal = baseSystem.toDecimal(wholePart);
    let wholeRational = new Rational(wholeDecimal);
    if (isNegative) {
      wholeRational = wholeRational.negate();
    }

    // Parse fractional part
    const fractionResult = parseBaseNotation(fractionPart, baseSystem, options);
    let fractionRational;

    if (fractionResult instanceof Integer) {
      fractionRational = fractionResult.toRational();
    } else if (fractionResult instanceof Rational) {
      fractionRational = fractionResult;
    } else {
      throw new Error("Mixed number fractional part must be a simple fraction");
    }

    // Combine whole and fractional parts
    // Handle sign: if whole part is negative, subtract the fraction
    if (wholeRational.numerator < 0n) {
      const result = wholeRational.subtract(fractionRational.abs());
      return options.typeAware && result.denominator === 1n
        ? new Integer(result.numerator)
        : result;
    } else {
      const result = wholeRational.add(fractionRational);
      return options.typeAware && result.denominator === 1n
        ? new Integer(result.numerator)
        : result;
    }
  }

  // Check for fraction notation (contains slash)
  if (baseNumber.includes("/")) {
    const parts = baseNumber.split("/");
    if (parts.length !== 2) {
      throw new Error('Fraction notation must have exactly one "/" separator');
    }

    const numeratorStr = parts[0].trim();
    const denominatorStr = parts[1].trim();

    // Parse numerator and denominator recursively to handle base inheritance and overrides
    const numeratorResult = parseBaseNotation(numeratorStr, baseSystem, options);
    const denominatorResult = parseBaseNotation(denominatorStr, baseSystem, options);

    // Convert to Rational for division
    const numRat = numeratorResult instanceof Integer ? numeratorResult.toRational() : numeratorResult;
    const denRat = denominatorResult instanceof Integer ? denominatorResult.toRational() : denominatorResult;

    if (denRat.numerator === 0n) {
      throw new Error("Denominator cannot be zero");
    }

    let result = numRat.divide(denRat);
    if (isNegative) {
      result = result.negate();
    }
    result._explicitFraction = true;
    return result;
  }

  // Check for decimal notation (contains single dot)
  if (baseNumber.includes(".")) {
    const parts = baseNumber.split(".");
    if (parts.length !== 2) {
      throw new Error('Decimal notation must have exactly one "." separator');
    }

    const integerPart = parts[0] || "0";
    const fractionalPart = parts[1] || "";

    // Allow trailing dot as shorthand for .0 (useful in uncertainty notation)

    // Validate all characters are valid for this base
    const fullStr = integerPart + fractionalPart;
    if (!baseSystem.isValidString(fullStr)) {
      throw new Error(
        `String "${baseNumber}" contains characters not valid for ${baseSystem.name}`,
      );
    }

    // Convert integer part to decimal
    const integerDecimal = baseSystem.toDecimal(integerPart);

    // Convert fractional part to decimal
    // Each digit in position i contributes digit_value / base^(i+1)
    let fractionalDecimal = 0n;
    const baseBigInt = BigInt(baseSystem.base);

    for (let i = 0; i < fractionalPart.length; i++) {
      const digitChar = fractionalPart[i];
      const digitValue = BigInt(baseSystem.charMap.get(digitChar));

      // Add digit_value / base^(i+1) to fractional part
      // This is implemented as: fractionalDecimal * base + digitValue
      fractionalDecimal = fractionalDecimal * baseBigInt + digitValue;
    }

    // Create the rational: integerPart + fractionalPart / base^fractionalLength
    const denominator = baseBigInt ** BigInt(fractionalPart.length);
    const totalNumerator = integerDecimal * denominator + fractionalDecimal;

    let result = new Rational(totalNumerator, denominator);
    if (isNegative) {
      result = result.negate();
    }
    return options.typeAware && result.denominator === 1n
      ? new Integer(result.numerator)
      : result;
  }

  // Simple integer case
  if (!baseSystem.isValidString(baseNumber)) {
    throw new Error(
      `String "${baseNumber}" contains characters not valid for ${baseSystem.name}`,
    );
  }

  let decimalValue = baseSystem.toDecimal(baseNumber);
  if (isNegative) {
    decimalValue = -decimalValue;
  }

  if (options.typeAware) {
    return new Integer(decimalValue);
  } else {
    return new Rational(decimalValue);
  }
}

export class Parser {
  /**
   * Parses a string representing an interval arithmetic expression
   *
   * @param {string} expression - The expression to parse
   * @param {Object} options - Parsing options
   * @param {boolean} options.typeAware - If true, returns Integer/Rational/RationalInterval based on input
   * @param {BaseSystem} options.inputBase - Base system for parsing input (default: decimal)
   * @returns {Integer|Rational|RationalInterval} The result of evaluating the expression
   * @throws {Error} If the expression syntax is invalid
   */
  static parse(expression, options = {}) {
    if (!expression || expression.trim() === "") {
      throw new Error("Expression cannot be empty");
    }

    // Set default value for typeAware
    options = { typeAware: true, ...options };

    // Handle space-sensitive E notation before removing whitespace
    // Replace " E" with "TE" (temporary marker) to preserve space information
    expression = expression.replace(/ E/g, "TE");

    // Handle space-sensitive division disambiguation before removing whitespace
    // Replace "/ " with "/S" (temporary marker) to preserve space information
    expression = expression.replace(/\/ /g, "/S");

    // Remove whitespace context-aware (Preserve Strings)
    let cleanExpr = "";
    let inString = false;
    let i = 0;
    while (i < expression.length) {
      const c = expression[i];
      if (c === '"') {
        // Toggle string state, handle escaped quotes if inside
        if (inString) {
          // check for escaped quote
          let backslashCount = 0;
          let j = i - 1;
          while (j >= 0 && expression[j] === '\\') {
            backslashCount++;
            j--;
          }
          if (backslashCount % 2 === 0) {
            inString = false;
          }
        } else {
          inString = true;
        }
        cleanExpr += c;
      } else {
        if (inString) {
          cleanExpr += c;
        } else if (!/\s/.test(c)) {
          cleanExpr += c;
        }
      }
      i++;
    }
    expression = cleanExpr;

    // Parse the expression (starting with logical OR - lowest precedence)
    const result = Parser.#parseOr(expression, options);

    if (result.remainingExpr.length > 0) {
      throw new Error(`Unexpected token at end: ${result.remainingExpr}`);
    }

    return result.value;
  }

  /**
   * Parses logical OR (||)
   * Lowest precedence
   * @private
   */
  static #parseOr(expr, options = {}) {
    let result = Parser.#parseAnd(expr, options);
    let currentExpr = result.remainingExpr;

    while (currentExpr.startsWith('||')) {
      currentExpr = currentExpr.substring(2);
      const rightResult = Parser.#parseAnd(currentExpr, options);
      currentExpr = rightResult.remainingExpr;

      // OR: returns 1 if either is truthy
      const leftTruthy = Parser.#isTruthy(result.value);
      const rightTruthy = Parser.#isTruthy(rightResult.value);
      result.value = new Integer(leftTruthy || rightTruthy ? 1n : 0n);
    }

    return {
      value: result.value,
      remainingExpr: currentExpr,
    };
  }

  /**
   * Parses logical AND (&&)
   * @private
   */
  static #parseAnd(expr, options = {}) {
    let result = Parser.#parseComparison(expr, options);
    let currentExpr = result.remainingExpr;

    while (currentExpr.startsWith('&&')) {
      currentExpr = currentExpr.substring(2);
      const rightResult = Parser.#parseComparison(currentExpr, options);
      currentExpr = rightResult.remainingExpr;

      // AND: returns 1 only if both are truthy
      const leftTruthy = Parser.#isTruthy(result.value);
      const rightTruthy = Parser.#isTruthy(rightResult.value);
      result.value = new Integer(leftTruthy && rightTruthy ? 1n : 0n);
    }

    return {
      value: result.value,
      remainingExpr: currentExpr,
    };
  }

  /**
   * Helper to check if a value is truthy (non-zero)
   * @private
   */
  static #isTruthy(val) {
    if (val instanceof Integer) {
      return val.value !== 0n;
    } else if (val instanceof Rational) {
      return val.numerator !== 0n;
    } else if (typeof val === 'number') {
      return val !== 0;
    } else if (typeof val === 'bigint') {
      return val !== 0n;
    }
    return Boolean(val);
  }

  /**
   * Parses comparison operators (<, >, <=, >=, ==, !=)
   * @private
   */
  static #parseComparison(expr, options = {}) {
    let result = Parser.#parseAddSub(expr, options);
    let currentExpr = result.remainingExpr;

    // Check for comparison operators (longest match first)
    const comparisonOps = ['<=', '>=', '==', '!=', '<', '>'];

    while (currentExpr.length > 0) {
      let matchedOp = null;
      for (const op of comparisonOps) {
        if (currentExpr.startsWith(op)) {
          matchedOp = op;
          break;
        }
      }

      if (!matchedOp) break;

      currentExpr = currentExpr.substring(matchedOp.length);
      const rightResult = Parser.#parseAddSub(currentExpr, options);
      currentExpr = rightResult.remainingExpr;

      // Evaluate comparison - returns Integer(1) for true, Integer(0) for false
      const left = result.value;
      const right = rightResult.value;
      let compResult;

      if (matchedOp === '==') {
        if (left.equals && right.equals) {
          compResult = left.equals(right) ? 1n : 0n;
        } else {
          compResult = left === right ? 1n : 0n;
        }
      } else if (matchedOp === '!=') {
        if (left.equals && right.equals) {
          compResult = !left.equals(right) ? 1n : 0n;
        } else {
          compResult = left !== right ? 1n : 0n;
        }
      } else if (matchedOp === '<' || matchedOp === '<=' || matchedOp === '>' || matchedOp === '>=') {
        // Use subtract to compare (works for Rational/Integer)
        let diff;
        if (left.subtract && typeof left.subtract === 'function') {
          diff = left.subtract(right);
        } else {
          // Fallback for primitive comparison
          const leftVal = Number(left);
          const rightVal = Number(right);
          if (matchedOp === '<') compResult = leftVal < rightVal ? 1n : 0n;
          else if (matchedOp === '<=') compResult = leftVal <= rightVal ? 1n : 0n;
          else if (matchedOp === '>') compResult = leftVal > rightVal ? 1n : 0n;
          else if (matchedOp === '>=') compResult = leftVal >= rightVal ? 1n : 0n;
          result.value = new Integer(compResult);
          continue;
        }

        const sign = diff.sign ? diff.sign() : (diff.numerator > 0n ? 1 : diff.numerator < 0n ? -1 : 0);
        if (matchedOp === '<') compResult = sign < 0 ? 1n : 0n;
        else if (matchedOp === '<=') compResult = sign <= 0 ? 1n : 0n;
        else if (matchedOp === '>') compResult = sign > 0 ? 1n : 0n;
        else if (matchedOp === '>=') compResult = sign >= 0 ? 1n : 0n;
      }

      result.value = new Integer(compResult);
    }

    return {
      value: result.value,
      remainingExpr: currentExpr,
    };
  }

  /**
   * Parses an expression with addition and subtraction
   * @private
   */
  static #parseAddSub(expr, options = {}) {
    let result = Parser.#parseTerm(expr, options);
    let currentExpr = result.remainingExpr;

    while (
      currentExpr.length > 0 &&
      (currentExpr[0] === "+" || currentExpr[0] === "-")
    ) {
      const operator = currentExpr[0];
      currentExpr = currentExpr.substring(1);

      const termResult = Parser.#parseTerm(currentExpr, options);
      currentExpr = termResult.remainingExpr;

      if (operator === "+") {
        result.value = result.value.add(termResult.value);
      } else {
        result.value = result.value.subtract(termResult.value);
      }
    }

    return {
      value: Parser.#promoteType(result.value, options),
      remainingExpr: currentExpr,
    };
  }

  /**
   * Parses a term with multiplication, division, and E notation
   * @private
   */
  static #parseTerm(expr, options = {}) {
    let result = Parser.#parseFactor(expr, options);
    let currentExpr = result.remainingExpr;

    while (
      currentExpr.length > 0 &&
      (currentExpr[0] === "*" ||
        currentExpr[0] === "/" ||
        currentExpr[0] === "E" ||
        currentExpr.startsWith("TE"))
    ) {
      let operator, skipLength;
      if (currentExpr.startsWith("TE")) {
        operator = "E";
        skipLength = 2;
      } else {
        operator = currentExpr[0];
        skipLength = 1;
      }
      currentExpr = currentExpr.substring(skipLength);

      // Handle space marker for division
      if (
        operator === "/" &&
        currentExpr.length > 0 &&
        currentExpr[0] === "S"
      ) {
        currentExpr = currentExpr.substring(1); // Skip the 'S' marker
      }

      const factorResult = Parser.#parseFactor(currentExpr, options);
      currentExpr = factorResult.remainingExpr;

      if (operator === "*") {
        result.value = result.value.multiply(factorResult.value);
      } else if (operator === "/") {
        result.value = result.value.divide(factorResult.value);
      } else if (operator === "E") {
        // E notation: left operand * 10^(right operand)
        // Extract the exponent value based on the type
        let exponentValue;
        if (factorResult.value instanceof Integer) {
          exponentValue = factorResult.value.value;
        } else if (factorResult.value instanceof Rational) {
          if (factorResult.value.denominator !== 1n) {
            throw new Error("E notation exponent must be an integer");
          }
          exponentValue = factorResult.value.numerator;
        } else if (factorResult.value.low && factorResult.value.high) {
          // RationalInterval case
          if (!factorResult.value.low.equals(factorResult.value.high)) {
            throw new Error("E notation exponent must be an integer");
          }
          const exponent = factorResult.value.low;
          if (exponent.denominator !== 1n) {
            throw new Error("E notation exponent must be an integer");
          }
          exponentValue = exponent.numerator;
        } else {
          throw new Error("Invalid E notation exponent type");
        }

        // Apply E notation using the value's E method if available
        if (result.value.E && typeof result.value.E === "function") {
          result.value = result.value.E(exponentValue);
        } else {
          // Fallback for backward compatibility
          const powerOf10 =
            exponentValue >= 0n
              ? new Rational(10n ** exponentValue)
              : new Rational(1n, 10n ** -exponentValue);
          const powerInterval = RationalInterval.point(powerOf10);
          result.value = result.value.multiply(powerInterval);
        }
      }
    }

    return {
      value: Parser.#promoteType(result.value, options),
      remainingExpr: currentExpr,
    };
  }

  /**
   * Parses a string literal: "..."
   * Handles escape sequences: \" \\ \uXXXX.
   * @private
   */
  static #parseStringLiteral(expr) {
    // Current expr starts with "
    let i = 1;
    let result = "";
    while (i < expr.length) {
      const char = expr[i];
      if (char === '"') {
        // End of string
        return {
          value: result, // Return generic string (treated as value)
          remainingExpr: expr.substring(i + 1),
        };
      } else if (char === '\\') {
        // Escape sequence
        i++;
        if (i >= expr.length) throw new Error("Unterminated string literal (trailing backslash)");
        const nextChar = expr[i];
        if (nextChar === '"') result += '"';
        else if (nextChar === '\\') result += '\\';
        else if (nextChar === 'u') {
          // Unicode \uXXXX
          if (i + 4 >= expr.length) throw new Error("Invalid unicode escape sequence");
          const hex = expr.substring(i + 1, i + 5);
          if (!/^[0-9a-fA-F]{4}$/.test(hex)) throw new Error("Invalid unicode escape sequence");
          result += String.fromCharCode(parseInt(hex, 16));
          i += 4;
        } else {
          // preserve literal backslash if not special? 
          // User said: "usual escape key of blackslash is used: \" is a literal quote and \\ is literal backslash. and \u... Otherwise, the string is literal."
          // So \a becomes a
          result += nextChar;
        }
      } else {
        result += char;
      }
      i++;
    }
    throw new Error("Unterminated string literal");
  }

  /**
   * Parses a list literal: [...]
   * Recursively parses elements separated by comma.
   * @private
   */
  static #parseListLiteral(expr, options) {
    // Current expr starts with [
    let currentExpr = expr.substring(1).trim(); // Skip [ and trim
    // Note: Parser.parse removes whitespace globally, but inside parseFactor we operate on a string that had whitespace removed already?
    // Parser.parse (line 949) does `expression = expression.replace(/\s+/g, "");`.
    // So `[a, b]` becomes `[a,b]`. 
    // Except strings! 
    // Wait. Global whitespace removal RUINS string literals.
    // "Hello World" -> "HelloWorld".
    // 
    // CRITICAL ISSUE: Parser.parse strips logic.
    // We must fix Parser.parse to respect strings/lists preservation.
    // 
    // For now, let's implement the logic assuming we receive clean input (or stripped input).
    // I will need to revisit `Parser.parse` to make whitespace removal context-aware.

    const values = [];

    // Check for empty list []
    if (currentExpr.startsWith("]")) {
      return {
        value: { type: 'sequence', values: [] },
        remainingExpr: currentExpr.substring(1)
      };
    }

    while (true) {
      // Parse expression for element
      // Using parseExpression? Or parseTerm? List elements can be expressions "1+2".
      const elemResult = Parser.#parseAddSub(currentExpr, options);
      values.push(elemResult.value);
      currentExpr = elemResult.remainingExpr;

      if (currentExpr.length === 0) throw new Error("Unterminated list literal");

      if (currentExpr[0] === ',') {
        currentExpr = currentExpr.substring(1);
        // Continue loop
      } else if (currentExpr[0] === ']') {
        currentExpr = currentExpr.substring(1);
        break;
      } else {
        throw new Error(`Unexpected token in list: ${currentExpr[0]}`);
      }
    }

    return {
      value: { type: 'sequence', values: values },
      remainingExpr: currentExpr
    };
  }

  /**
   * Parses a factor (interval, number, or parenthesized expression)
   * @private
   */
  static #parseFactor(expr, options = {}) {
    if (expr.length === 0) {
      throw new Error("Unexpected end of expression");
    }

    // Handle parenthesized expressions
    if (expr[0] === "(") {
      const subExprResult = Parser.#parseAddSub(expr.substring(1), options);

      if (
        subExprResult.remainingExpr.length === 0 ||
        subExprResult.remainingExpr[0] !== ")"
      ) {
        throw new Error("Missing closing parenthesis");
      }

      const result = {
        value: subExprResult.value,
        remainingExpr: subExprResult.remainingExpr.substring(1),
      };

      // Check for tight E notation after the closing parenthesis (higher precedence than exponentiation)
      if (
        result.remainingExpr.length > 0 &&
        (result.remainingExpr[0] === "E" ||
          result.remainingExpr.startsWith("TE") ||
          result.remainingExpr.startsWith("_^"))
      ) {
        const eResult = Parser.#parseENotation(
          result.value,
          result.remainingExpr,
          options,
        );

        // Check for factorial operators after E notation (higher precedence than exponentiation)
        let factorialResult = eResult;
        if (
          factorialResult.remainingExpr.length > 1 &&
          factorialResult.remainingExpr.substring(0, 2) === "!!"
        ) {
          // Double factorial
          if (factorialResult.value instanceof Integer) {
            factorialResult = {
              value: factorialResult.value.doubleFactorial(),
              remainingExpr: factorialResult.remainingExpr.substring(2),
            };
          } else if (
            factorialResult.value instanceof Rational &&
            factorialResult.value.denominator === 1n
          ) {
            const intValue = new Integer(factorialResult.value.numerator);
            factorialResult = {
              value: intValue.doubleFactorial().toRational(),
              remainingExpr: factorialResult.remainingExpr.substring(2),
            };
          } else if (
            factorialResult.value.low &&
            factorialResult.value.high &&
            factorialResult.value.low.equals(factorialResult.value.high) &&
            factorialResult.value.low.denominator === 1n
          ) {
            // Point interval containing an integer
            const intValue = new Integer(factorialResult.value.low.numerator);
            const factorialValue = intValue.doubleFactorial();
            const IntervalClass = factorialResult.value.constructor;
            factorialResult = {
              value: new IntervalClass(
                factorialValue.toRational(),
                factorialValue.toRational(),
              ),
              remainingExpr: factorialResult.remainingExpr.substring(2),
            };
          } else {
            throw new Error(
              "Double factorial is not defined for negative integers",
            );
          }
        } else if (
          factorialResult.remainingExpr.length > 0 &&
          factorialResult.remainingExpr[0] === "!" &&
          factorialResult.remainingExpr[1] !== "="  // Don't consume ! if it's part of !=
        ) {
          // Single factorial
          if (factorialResult.value instanceof Integer) {
            factorialResult = {
              value: factorialResult.value.factorial(),
              remainingExpr: factorialResult.remainingExpr.substring(1),
            };
          } else if (
            factorialResult.value instanceof Rational &&
            factorialResult.value.denominator === 1n
          ) {
            const intValue = new Integer(factorialResult.value.numerator);
            factorialResult = {
              value: intValue.factorial().toRational(),
              remainingExpr: factorialResult.remainingExpr.substring(1),
            };
          } else if (
            factorialResult.value.low &&
            factorialResult.value.high &&
            factorialResult.value.low.equals(factorialResult.value.high) &&
            factorialResult.value.low.denominator === 1n
          ) {
            // Point interval containing an integer
            const intValue = new Integer(factorialResult.value.low.numerator);
            const factorialValue = intValue.factorial();
            const IntervalClass = factorialResult.value.constructor;
            factorialResult = {
              value: new IntervalClass(
                factorialValue.toRational(),
                factorialValue.toRational(),
              ),
              remainingExpr: factorialResult.remainingExpr.substring(1),
            };
          } else {
            throw new Error("Factorial is not defined for negative integers");
          }
        }

        // Check for exponentiation after factorial
        if (factorialResult.remainingExpr.length > 0) {
          if (factorialResult.remainingExpr[0] === "^") {
            // Standard exponentiation (pow)
            const powerExpr = factorialResult.remainingExpr.substring(1);

            // First check if it's a simple integer exponent
            let powerResult;
            let isIntegerExponent = false;

            try {
              // Try parsing as integer first
              powerResult = Parser.#parseExponent(powerExpr);
              isIntegerExponent = true;
            } catch (e) {
              // If not a simple integer, parse as expression
              powerResult = Parser.#parseExponentExpression(powerExpr, options);
              isIntegerExponent = false;
            }

            // Check for 0^0
            const zero = new Rational(0);
            const isZeroBase = factorialResult.value.low && factorialResult.value.high ?
              (factorialResult.value.low.equals(zero) && factorialResult.value.high.equals(zero)) :
              (factorialResult.value instanceof Integer && factorialResult.value.value === 0n) ||
              (factorialResult.value instanceof Rational && factorialResult.value.numerator === 0n);

            const isZeroExponent = isIntegerExponent ?
              powerResult.value === 0n :
              (powerResult.value instanceof Rational && powerResult.value.numerator === 0n) ||
              (powerResult.value instanceof Integer && powerResult.value.value === 0n);

            if (isZeroBase && isZeroExponent) {
              throw new Error("Zero cannot be raised to the power of zero");
            }

            let result;
            if (isIntegerExponent) {
              // Use standard pow for integer exponents
              result = factorialResult.value.pow(powerResult.value);
            } else {
              // Use rationalIntervalPower for fractional exponents
              const precision = options.precision || DEFAULT_PRECISION;
              result = rationalIntervalPower(factorialResult.value, powerResult.value, precision);
            }

            return {
              value: result,
              remainingExpr: powerResult.remainingExpr,
            };
          } else if (
            factorialResult.remainingExpr.length > 1 &&
            factorialResult.remainingExpr[0] === "*" &&
            factorialResult.remainingExpr[1] === "*"
          ) {
            // Multiplicative exponentiation (mpow) or Newton root (**)
            const powerExpr = factorialResult.remainingExpr.substring(2);

            // First check if it's a simple integer exponent
            let powerResult;
            let isIntegerExponent = false;

            try {
              // Try parsing as integer first
              powerResult = Parser.#parseExponent(powerExpr);
              isIntegerExponent = true;
            } catch (e) {
              // If not a simple integer, parse as expression
              powerResult = Parser.#parseExponentExpression(powerExpr, options);
              isIntegerExponent = false;
            }

            // Check for zero exponent in multiplicative exponentiation
            const isZeroExponent = (isIntegerExponent && powerResult.value === 0n) ||
              (!isIntegerExponent && powerResult.value instanceof Integer && powerResult.value.value === 0n) ||
              (!isIntegerExponent && powerResult.value instanceof Rational && powerResult.value.numerator === 0n);

            if (isZeroExponent) {
              throw new Error("Multiplicative exponentiation requires at least one factor");
            }

            let result;
            if (!isIntegerExponent && powerResult.value instanceof Rational &&
              Number(powerResult.value.denominator) <= 10 &&
              Number(powerResult.value.denominator) > 1) {
              // Use Newton's method for rational exponents with small denominators
              const precision = options.precision || DEFAULT_PRECISION;
              const rootDegree = Number(powerResult.value.denominator);
              const rootInterval = newtonRoot(factorialResult.value, rootDegree, precision);

              // If numerator is not 1, raise to numerator power
              if (!powerResult.value.numerator === 1n) {
                const numeratorPower = Number(powerResult.value.numerator);
                result = rootInterval;
                for (let i = 1; i < Math.abs(numeratorPower); i++) {
                  result = result.multiply(rootInterval);
                }
                if (numeratorPower < 0) {
                  // For negative powers, take reciprocal
                  result = new RationalInterval(
                    new Rational(1).divide(result.upper),
                    new Rational(1).divide(result.lower)
                  );
                }
              } else {
                result = rootInterval;
              }
            } else if (isIntegerExponent) {
              // For multiplicative exponentiation with integer exponents, use mpow
              let base = factorialResult.value;
              if (!(base instanceof RationalInterval)) {
                // Convert scalar to point interval for mpow
                base = RationalInterval.point(
                  base instanceof Integer ? base.toRational() : base,
                );
              }
              result = base.mpow(powerResult.value);
            } else {
              // For general fractional exponents, use rationalIntervalPower
              const precision = options.precision || DEFAULT_PRECISION;
              result = rationalIntervalPower(factorialResult.value, powerResult.value, precision);
            }

            if (result._skipPromotion === undefined) {
              result._skipPromotion = true;
            }
            return {
              value: result,
              remainingExpr: powerResult.remainingExpr,
            };
          }
        }

        return factorialResult;
      }

      // Check for factorial operators after the closing parenthesis (higher precedence than exponentiation)
      let factorialResult = result;
      if (
        factorialResult.remainingExpr.length > 1 &&
        factorialResult.remainingExpr.substring(0, 2) === "!!"
      ) {
        // Double factorial
        if (factorialResult.value instanceof Integer) {
          factorialResult = {
            value: factorialResult.value.doubleFactorial(),
            remainingExpr: factorialResult.remainingExpr.substring(2),
          };
        } else if (
          factorialResult.value instanceof Rational &&
          factorialResult.value.denominator === 1n
        ) {
          const intValue = new Integer(factorialResult.value.numerator);
          factorialResult = {
            value: intValue.doubleFactorial().toRational(),
            remainingExpr: factorialResult.remainingExpr.substring(2),
          };
        } else if (
          factorialResult.value.low &&
          factorialResult.value.high &&
          factorialResult.value.low.equals(factorialResult.value.high) &&
          factorialResult.value.low.denominator === 1n
        ) {
          // Point interval containing an integer
          const intValue = new Integer(factorialResult.value.low.numerator);
          const factorialValue = intValue.doubleFactorial();
          const IntervalClass = factorialResult.value.constructor;
          factorialResult = {
            value: new IntervalClass(
              factorialValue.toRational(),
              factorialValue.toRational(),
            ),
            remainingExpr: factorialResult.remainingExpr.substring(2),
          };
        } else {
          throw new Error(
            "Double factorial is not defined for negative integers",
          );
        }
      } else if (
        factorialResult.remainingExpr.length > 0 &&
        factorialResult.remainingExpr[0] === "!" &&
        factorialResult.remainingExpr[1] !== "="  // Don't consume ! if it's part of !=
      ) {
        // Single factorial
        if (factorialResult.value instanceof Integer) {
          factorialResult = {
            value: factorialResult.value.factorial(),
            remainingExpr: factorialResult.remainingExpr.substring(1),
          };
        } else if (
          factorialResult.value instanceof Rational &&
          factorialResult.value.denominator === 1n
        ) {
          const intValue = new Integer(factorialResult.value.numerator);
          factorialResult = {
            value: intValue.factorial().toRational(),
            remainingExpr: factorialResult.remainingExpr.substring(1),
          };
        } else if (
          factorialResult.value.low &&
          factorialResult.value.high &&
          factorialResult.value.low.equals(factorialResult.value.high) &&
          factorialResult.value.low.denominator === 1n
        ) {
          // Point interval containing an integer
          const intValue = new Integer(factorialResult.value.low.numerator);
          const factorialValue = intValue.factorial();
          const IntervalClass = factorialResult.value.constructor;
          factorialResult = {
            value: new IntervalClass(
              factorialValue.toRational(),
              factorialValue.toRational(),
            ),
            remainingExpr: factorialResult.remainingExpr.substring(1),
          };
        } else {
          throw new Error("Factorial is not defined for negative integers");
        }
      }

      // Check for exponentiation after factorial
      if (factorialResult.remainingExpr.length > 0) {
        if (factorialResult.remainingExpr[0] === "^") {
          // Standard exponentiation (pow)
          const powerExpr = factorialResult.remainingExpr.substring(1);

          // First check if it's a simple integer exponent
          let powerResult;
          let isIntegerExponent = false;

          try {
            // Try parsing as integer first
            powerResult = Parser.#parseExponent(powerExpr);
            isIntegerExponent = true;
          } catch (e) {
            // If not a simple integer, parse as expression
            powerResult = Parser.#parseExponentExpression(powerExpr, options);
            isIntegerExponent = false;
          }

          // Check for 0^0
          const zero = new Rational(0);
          let isZero = false;

          if (factorialResult.value instanceof RationalInterval) {
            isZero =
              factorialResult.value.low.equals(zero) &&
              factorialResult.value.high.equals(zero);
          } else if (factorialResult.value instanceof Rational) {
            isZero = factorialResult.value.equals(zero);
          } else if (factorialResult.value instanceof Integer) {
            isZero = factorialResult.value.value === 0n;
          }

          const isZeroExponent = isIntegerExponent ?
            powerResult.value === 0n :
            (powerResult.value instanceof Rational && powerResult.value.numerator === 0n) ||
            (powerResult.value instanceof Integer && powerResult.value.value === 0n);

          if (isZero && isZeroExponent) {
            throw new Error("Zero cannot be raised to the power of zero");
          }

          let result;
          if (isIntegerExponent) {
            // Use standard pow for integer exponents
            result = factorialResult.value.pow(powerResult.value);
          } else {
            // Use rationalIntervalPower for fractional exponents
            const precision = options.precision || DEFAULT_PRECISION;
            result = rationalIntervalPower(factorialResult.value, powerResult.value, precision);
          }

          return {
            value: result,
            remainingExpr: powerResult.remainingExpr,
          };
        } else if (
          factorialResult.remainingExpr.length > 1 &&
          factorialResult.remainingExpr[0] === "*" &&
          factorialResult.remainingExpr[1] === "*"
        ) {
          // Multiplicative exponentiation (mpow) or Newton root (**)
          const powerExpr = factorialResult.remainingExpr.substring(2);

          // First check if it's a simple integer exponent
          let powerResult;
          let isIntegerExponent = false;

          try {
            // Try parsing as integer first
            powerResult = Parser.#parseExponent(powerExpr);
            isIntegerExponent = true;
          } catch (e) {
            // If not a simple integer, parse as expression
            powerResult = Parser.#parseExponentExpression(powerExpr, options);
            isIntegerExponent = false;
          }

          // Check for zero exponent in multiplicative exponentiation
          const isZeroExponent = (isIntegerExponent && powerResult.value === 0n) ||
            (!isIntegerExponent && powerResult.value instanceof Integer && powerResult.value.value === 0n) ||
            (!isIntegerExponent && powerResult.value instanceof Rational && powerResult.value.numerator === 0n);

          if (isZeroExponent) {
            throw new Error("Multiplicative exponentiation requires at least one factor");
          }

          let result;
          if (!isIntegerExponent && powerResult.value instanceof Rational &&
            Number(powerResult.value.denominator) <= 10 &&
            Number(powerResult.value.denominator) > 1) {
            // Use Newton's method for rational exponents with small denominators
            const precision = options.precision || DEFAULT_PRECISION;
            const rootDegree = Number(powerResult.value.denominator);
            const rootInterval = newtonRoot(factorialResult.value, rootDegree, precision);

            // If numerator is not 1, raise to numerator power
            if (!powerResult.value.numerator === 1n) {
              const numeratorPower = Number(powerResult.value.numerator);
              result = rootInterval;
              for (let i = 1; i < Math.abs(numeratorPower); i++) {
                result = result.multiply(rootInterval);
              }
              if (numeratorPower < 0) {
                // For negative powers, take reciprocal
                result = new RationalInterval(
                  new Rational(1).divide(result.upper),
                  new Rational(1).divide(result.lower)
                );
              }
            } else {
              result = rootInterval;
            }
          } else if (isIntegerExponent) {
            // For multiplicative exponentiation with integer exponents, use mpow
            let base = factorialResult.value;
            if (!(base instanceof RationalInterval)) {
              // Convert scalar to point interval for mpow
              base = RationalInterval.point(
                base instanceof Integer ? base.toRational() : base,
              );
            }
            result = base.mpow(powerResult.value);
          } else {
            // For general fractional exponents, use rationalIntervalPower
            const precision = options.precision || DEFAULT_PRECISION;
            result = rationalIntervalPower(factorialResult.value, powerResult.value, precision);
          }

          if (result._skipPromotion === undefined) {
            result._skipPromotion = true;
          }
          return {
            value: result,
            remainingExpr: powerResult.remainingExpr,
          };
        }
      }

      return factorialResult;
    }

    // Check for String Literal
    if (expr[0] === '"') {
      const stringResult = Parser.#parseStringLiteral(expr);
      return stringResult;
    }

    // Check for List Literal
    if (expr[0] === "[") {
      // Disambiguate with uncertainty notation or base notation
      // Uncertainty/Interval usually follows a number. parseFactor handles START of factor.
      // However, [1,2] could be a list OR an interval?
      // user said "Lists... literal... [...]"
      // [1, 2, 3] is list.
      // [1:5] is interval? or list?
      // If it contains comma, likely list (unless uncertainty rel/range).
      // But uncertainty is suffix.
      // Standalone [a,b] is list.
      // Standalone [a:b] might be interval?
      // Existing parser handles intervals via `parseInterval` which calls `parseBaseNotation`.
      // `parseBaseNotation` handles `1:5`.
      // `parseInterval` calls `parseBaseNotation`.
      // If we see `[`, it is likely a list.
      // Intervals in RatMath are usually `a:b` or `1.2[3]` or `1.2[+1]`.
      // Standalone `[1,2]` is NOT currently valid interval syntax in RatMath (it uses `1:2`).
      // So `[` at start of factor is safely a List.

      const listResult = Parser.#parseListLiteral(expr, options);
      return listResult;
    }

    // Check for base notation first (like 101[2], FF[16], A[16]:F[16], etc.)
    if (expr.includes("[") && expr.includes("]")) {
      // Check if it matches base notation pattern specifically
      const baseMatch = expr.match(/^([-\w./:^]+(?::[-\w./:^]+)?)\[(\d+)\]/);
      const isCustomBase = /^-*0z\[\d+\]/i.test(expr);
      if (baseMatch && !isCustomBase) {
        throw new Error(
          "Bracket base notation (Value[Base]) is no longer supported. Use prefix notation (0xValue, 0bValue) or the BASE command."
        );
      }

      // If not base notation, check for uncertainty notation
      const uncertaintyMatch = !isCustomBase ? expr.match(/^(-?[@\w./:^]+)\[([^\]]+)\]((?:[Ee][+-]?[\w]+|\_?\^-?[\w]+)?)/) : null;
      if (uncertaintyMatch) {
        const fullMatch = uncertaintyMatch[0];
        try {
          const result = parseDecimalUncertainty(fullMatch, options); // Allow integer range notation in Parser
          return {
            value: result,
            remainingExpr: expr.substring(fullMatch.length),
          };
        } catch (error) {
          // If it looks like uncertainty notation but is malformed, throw the error
          throw error;
        }
      }
    }

    // Check for negation (but only if it's not part of uncertainty notation or interval notation)
    const isCustomBasePrefix = /^-*0z\[\d+\]/i.test(expr);
    const isUncertainty = expr.includes("[") && !isCustomBasePrefix;
    if (expr[0] === "-" && !isUncertainty && !expr.includes(":")) {
      const factorResult = Parser.#parseFactor(expr.substring(1), options);

      // Negate the value - type-aware for new parsing, backward compatible for old
      let negatedValue;
      if (options.typeAware && factorResult.value instanceof Integer) {
        negatedValue = factorResult.value.negate();
      } else if (options.typeAware && factorResult.value instanceof Rational) {
        negatedValue = factorResult.value.negate();
        // Preserve explicitFraction flag when negating
        if (factorResult.value._explicitFraction) {
          negatedValue._explicitFraction = true;
        }
      } else {
        // For backward compatibility and intervals, negate by multiplying by -1
        const negOne = new Rational(-1);
        const negInterval = RationalInterval.point(negOne);
        negatedValue = negInterval.multiply(factorResult.value);
      }

      return {
        value: negatedValue,
        remainingExpr: factorResult.remainingExpr,
      };
    }

    // Try to parse a number (could be Integer, Rational, or RationalInterval)
    const numberResult = Parser.#parseInterval(expr, options);
    // Check for tight E notation after the number (higher precedence than exponentiation)
    if (
      numberResult.remainingExpr.length > 0 &&
      (numberResult.remainingExpr[0] === "E" ||
        numberResult.remainingExpr.startsWith("TE") ||
        numberResult.remainingExpr.startsWith("_^"))
    ) {
      const eResult = Parser.#parseENotation(
        numberResult.value,
        numberResult.remainingExpr,
        options,
      );

      // Check for factorial operators after E notation (higher precedence than exponentiation)
      let factorialResult = eResult;
      if (
        factorialResult.remainingExpr.length > 1 &&
        factorialResult.remainingExpr.substring(0, 2) === "!!"
      ) {
        // Double factorial
        if (factorialResult.value instanceof Integer) {
          factorialResult = {
            value: factorialResult.value.doubleFactorial(),
            remainingExpr: factorialResult.remainingExpr.substring(2),
          };
        } else if (
          factorialResult.value instanceof Rational &&
          factorialResult.value.denominator === 1n
        ) {
          const intValue = new Integer(factorialResult.value.numerator);
          factorialResult = {
            value: intValue.doubleFactorial().toRational(),
            remainingExpr: factorialResult.remainingExpr.substring(2),
          };
        } else if (
          factorialResult.value.low &&
          factorialResult.value.high &&
          factorialResult.value.low.equals(factorialResult.value.high) &&
          factorialResult.value.low.denominator === 1n
        ) {
          // Point interval containing an integer
          const intValue = new Integer(factorialResult.value.low.numerator);
          const factorialValue = intValue.doubleFactorial();
          const IntervalClass = factorialResult.value.constructor;
          factorialResult = {
            value: new IntervalClass(
              factorialValue.toRational(),
              factorialValue.toRational(),
            ),
            remainingExpr: factorialResult.remainingExpr.substring(2),
          };
        } else {
          throw new Error(
            "Double factorial is not defined for negative integers",
          );
        }
      } else if (
        factorialResult.remainingExpr.length > 0 &&
        factorialResult.remainingExpr[0] === "!" &&
        factorialResult.remainingExpr[1] !== "="  // Don't consume ! if it's part of !=
      ) {
        // Single factorial
        if (factorialResult.value instanceof Integer) {
          factorialResult = {
            value: factorialResult.value.factorial(),
            remainingExpr: factorialResult.remainingExpr.substring(1),
          };
        } else if (
          factorialResult.value instanceof Rational &&
          factorialResult.value.denominator === 1n
        ) {
          const intValue = new Integer(factorialResult.value.numerator);
          factorialResult = {
            value: intValue.factorial().toRational(),
            remainingExpr: factorialResult.remainingExpr.substring(1),
          };
        } else if (
          factorialResult.value.low &&
          factorialResult.value.high &&
          factorialResult.value.low.equals(factorialResult.value.high) &&
          factorialResult.value.low.denominator === 1n
        ) {
          // Point interval containing an integer
          const intValue = new Integer(factorialResult.value.low.numerator);
          const factorialValue = intValue.factorial();
          const IntervalClass = factorialResult.value.constructor;
          factorialResult = {
            value: new IntervalClass(
              factorialValue.toRational(),
              factorialValue.toRational(),
            ),
            remainingExpr: factorialResult.remainingExpr.substring(1),
          };
        } else {
          throw new Error("Factorial is not defined for negative integers");
        }
      }

      // Check for exponentiation after factorial
      if (factorialResult.remainingExpr.length > 0) {
        if (factorialResult.remainingExpr[0] === "^") {
          // Standard exponentiation (pow)
          const powerExpr = factorialResult.remainingExpr.substring(1);

          // First check if it's a simple integer exponent
          let powerResult;
          let isIntegerExponent = false;

          try {
            // Try parsing as integer first
            powerResult = Parser.#parseExponent(powerExpr);
            isIntegerExponent = true;
          } catch (e) {
            // If not a simple integer, parse as expression
            powerResult = Parser.#parseExponentExpression(powerExpr, options);
            isIntegerExponent = false;
          }

          // Check for 0^0
          const isZeroBase =
            (factorialResult.value instanceof Integer && factorialResult.value.value === 0n) ||
            (factorialResult.value instanceof Rational && factorialResult.value.numerator === 0n) ||
            (factorialResult.value.low && factorialResult.value.high &&
              factorialResult.value.low.equals(new Rational(0)) &&
              factorialResult.value.high.equals(new Rational(0)));

          const isZeroExponent = isIntegerExponent ?
            powerResult.value === 0n :
            (powerResult.value instanceof Rational && powerResult.value.numerator === 0n) ||
            (powerResult.value instanceof Integer && powerResult.value.value === 0n);

          if (isZeroBase && isZeroExponent) {
            throw new Error("Zero cannot be raised to the power of zero");
          }

          let result;
          if (isIntegerExponent) {
            // Use standard pow for integer exponents
            result = factorialResult.value.pow(powerResult.value);
          } else {
            // Use rationalIntervalPower for fractional exponents
            const precision = options.precision || DEFAULT_PRECISION;
            result = rationalIntervalPower(factorialResult.value, powerResult.value, precision);
          }

          return {
            value: result,
            remainingExpr: powerResult.remainingExpr,
          };
        } else if (
          factorialResult.remainingExpr.length > 1 &&
          factorialResult.remainingExpr[0] === "*" &&
          factorialResult.remainingExpr[1] === "*"
        ) {
          // Multiplicative exponentiation (mpow)
          const powerExpr = factorialResult.remainingExpr.substring(2);
          const powerResult = Parser.#parseExponent(powerExpr);

          // Check for zero exponent in multiplicative exponentiation
          const isZeroExponent = powerResult.value === 0n;

          if (isZeroExponent) {
            throw new Error("Multiplicative exponentiation requires at least one factor");
          }

          // For multiplicative exponentiation, always use mpow semantics
          let base = factorialResult.value;
          if (!(base instanceof RationalInterval)) {
            // Convert scalar to point interval for mpow
            base = RationalInterval.point(
              base instanceof Integer ? base.toRational() : base,
            );
          }
          const result = base.mpow(powerResult.value);
          // Don't promote multiplicative power results - they should stay as intervals
          result._skipPromotion = true;
          return {
            value: result,
            remainingExpr: powerResult.remainingExpr,
          };
        }
      }

      return factorialResult;
    }

    // Check for factorial operators (higher precedence than exponentiation)
    let factorialResult = numberResult;
    if (
      factorialResult.remainingExpr.length > 1 &&
      factorialResult.remainingExpr.substring(0, 2) === "!!"
    ) {
      // Double factorial
      if (factorialResult.value instanceof Integer) {
        factorialResult = {
          value: factorialResult.value.doubleFactorial(),
          remainingExpr: factorialResult.remainingExpr.substring(2),
        };
      } else if (
        factorialResult.value instanceof Rational &&
        factorialResult.value.denominator === 1n
      ) {
        const intValue = new Integer(factorialResult.value.numerator);
        factorialResult = {
          value: intValue.doubleFactorial().toRational(),
          remainingExpr: factorialResult.remainingExpr.substring(2),
        };
      } else if (
        factorialResult.value.low &&
        factorialResult.value.high &&
        factorialResult.value.low.equals(factorialResult.value.high) &&
        factorialResult.value.low.denominator === 1n
      ) {
        // Point interval containing an integer
        const intValue = new Integer(factorialResult.value.low.numerator);
        const factorialValue = intValue.doubleFactorial();
        const IntervalClass = factorialResult.value.constructor;
        factorialResult = {
          value: new IntervalClass(
            factorialValue.toRational(),
            factorialValue.toRational(),
          ),
          remainingExpr: factorialResult.remainingExpr.substring(2),
        };
      } else {
        throw new Error(
          "Double factorial is not defined for negative integers",
        );
      }
    } else if (
      factorialResult.remainingExpr.length > 0 &&
      factorialResult.remainingExpr[0] === "!" &&
      factorialResult.remainingExpr[1] !== "="  // Don't consume ! if it's part of !=
    ) {
      // Single factorial
      if (factorialResult.value instanceof Integer) {
        factorialResult = {
          value: factorialResult.value.factorial(),
          remainingExpr: factorialResult.remainingExpr.substring(1),
        };
      } else if (
        factorialResult.value instanceof Rational &&
        factorialResult.value.denominator === 1n
      ) {
        const intValue = new Integer(factorialResult.value.numerator);
        factorialResult = {
          value: intValue.factorial().toRational(),
          remainingExpr: factorialResult.remainingExpr.substring(1),
        };
      } else if (
        factorialResult.value.low &&
        factorialResult.value.high &&
        factorialResult.value.low.equals(factorialResult.value.high) &&
        factorialResult.value.low.denominator === 1n
      ) {
        // Point interval containing an integer
        const intValue = new Integer(factorialResult.value.low.numerator);
        const factorialValue = intValue.factorial();
        const IntervalClass = factorialResult.value.constructor;
        factorialResult = {
          value: new IntervalClass(
            factorialValue.toRational(),
            factorialValue.toRational(),
          ),
          remainingExpr: factorialResult.remainingExpr.substring(1),
        };
      } else {
        throw new Error("Factorial is not defined for negative integers");
      }
    }

    // Check for standard exponentiation after factorial
    if (factorialResult.remainingExpr.length > 0) {
      if (factorialResult.remainingExpr[0] === "^") {
        // Standard exponentiation (pow)
        const powerExpr = factorialResult.remainingExpr.substring(1);

        // Handle both integer and fractional exponents
        let powerResult;
        let isIntegerExponent = false;

        // Check if exponent starts with parentheses (fractional exponent)
        if (powerExpr.startsWith('(')) {
          powerResult = Parser.#parseAddSub(powerExpr.substring(1), options);
          if (powerResult.remainingExpr.length === 0 || powerResult.remainingExpr[0] !== ')') {
            throw new Error('Missing closing parenthesis in exponent');
          }
          powerResult.remainingExpr = powerResult.remainingExpr.substring(1);
          isIntegerExponent = false;
        } else {
          try {
            // Try parsing as integer first
            powerResult = Parser.#parseExponent(powerExpr);
            isIntegerExponent = true;
          } catch (e) {
            // If not a simple integer, parse as expression
            powerResult = Parser.#parseExponentExpression(powerExpr, options);
            isIntegerExponent = false;
          }
        }

        // Check for 0^0
        const isZeroBase =
          (factorialResult.value instanceof Integer && factorialResult.value.value === 0n) ||
          (factorialResult.value instanceof Rational && factorialResult.value.numerator === 0n) ||
          (factorialResult.value.low && factorialResult.value.high &&
            factorialResult.value.low.equals(new Rational(0)) &&
            factorialResult.value.high.equals(new Rational(0)));

        const isZeroExponent = isIntegerExponent ?
          powerResult.value === 0n :
          (powerResult.value instanceof Rational && powerResult.value.numerator === 0n) ||
          (powerResult.value instanceof Integer && powerResult.value.value === 0n);

        if (isZeroBase && isZeroExponent) {
          throw new Error("Zero cannot be raised to the power of zero");
        }

        let result;
        if (isIntegerExponent) {
          // Use standard pow for integer exponents
          result = factorialResult.value.pow(powerResult.value);
        } else {
          // Use rationalIntervalPower for fractional exponents
          const precision = options.precision || DEFAULT_PRECISION;
          result = rationalIntervalPower(factorialResult.value, powerResult.value, precision);
          result._skipPromotion = true;
        }
        return {
          value: result,
          remainingExpr: powerResult.remainingExpr,
        };
      } else if (
        factorialResult.remainingExpr.length > 1 &&
        factorialResult.remainingExpr[0] === "*" &&
        factorialResult.remainingExpr[1] === "*"
      ) {
        // Multiplicative exponentiation (mpow)
        const powerExpr = factorialResult.remainingExpr.substring(2);
        let powerResult;

        // Check if exponent starts with parentheses (fractional exponent)
        if (powerExpr.startsWith('(')) {
          powerResult = Parser.#parseAddSub(powerExpr.substring(1), options);
          if (powerResult.remainingExpr.length === 0 || powerResult.remainingExpr[0] !== ')') {
            throw new Error('Missing closing parenthesis in exponent');
          }
          powerResult.remainingExpr = powerResult.remainingExpr.substring(1);
        } else {
          // Try parsing as integer first
          try {
            powerResult = Parser.#parseExponent(powerExpr);
          } catch (e) {
            // If integer parsing fails, try parsing as general expression
            powerResult = Parser.#parseAddSub(powerExpr, options);
          }
        }

        // For multiplicative exponentiation, use rationalIntervalPower for fractional exponents
        let base = factorialResult.value;

        // Check if exponent is an integer
        const isIntegerExponent = (powerResult.value instanceof Integer) ||
          (powerResult.value instanceof Rational && powerResult.value.denominator === 1n);

        // Check for zero exponent in multiplicative exponentiation
        const isZeroExponent = (powerResult.value instanceof Integer && powerResult.value.value === 0n) ||
          (powerResult.value instanceof Rational && powerResult.value.numerator === 0n);

        if (isZeroExponent) {
          throw new Error("Multiplicative exponentiation requires at least one factor");
        }

        let result;
        if (isIntegerExponent) {
          // Use mpow for integer exponents
          if (!(base instanceof RationalInterval)) {
            base = RationalInterval.point(
              base instanceof Integer ? base.toRational() : base,
            );
          }
          const exponentBigInt = powerResult.value instanceof Integer ?
            powerResult.value.value : powerResult.value.numerator;
          result = base.mpow(exponentBigInt);
        } else {
          // Use rationalIntervalPower for fractional exponents
          const precision = options.precision || DEFAULT_PRECISION;
          result = rationalIntervalPower(base, powerResult.value, precision);
        }
        result._skipPromotion = true;
        return {
          value: result,
          remainingExpr: powerResult.remainingExpr,
        };
      }
    }

    return factorialResult;
  }

  /**
   * Parses an exponent (must be an integer)
   * @private
   */
  static #parseExponent(expr) {
    let i = 0;
    let isNegative = false;

    // Handle negative exponents
    if (expr.length > 0 && expr[0] === "-") {
      isNegative = true;
      i++;
    }

    // Parse the exponent digits
    let exponentStr = "";
    while (i < expr.length && /\d/.test(expr[i])) {
      exponentStr += expr[i];
      i++;
    }

    if (exponentStr.length === 0) {
      throw new Error("Invalid exponent");
    }

    // Convert to BigInt with proper sign
    const exponent = isNegative ? -BigInt(exponentStr) : BigInt(exponentStr);

    // Special handling for zero exponents in multiplicative exponentiation context
    // This method is called specifically for ** operators, so zero should throw an error
    if (exponent === 0n) {
      throw new Error("Multiplicative exponentiation requires at least one factor");
    }

    return {
      value: exponent,
      remainingExpr: expr.substring(i),
    };
  }

  static #parseExponentExpression(expr, options) {
    // For general exponents (including fractions), parse as a factor
    // This allows expressions like 2^(1/2) or 3^0.5
    return Parser.#parseFactor(expr, options);
  }

  /**
   * Promotes a value to the most appropriate type for type-aware parsing
   * @private
   */
  static #promoteType(value, options = {}) {
    if (!options.typeAware) {
      return value;
    }

    // Don't promote if skipPromotion flag is set
    if (value && value._skipPromotion) {
      return value;
    }

    // If it's a RationalInterval point containing a whole number, convert to Integer
    // BUT only if it wasn't explicitly parsed as an interval (has explicitInterval flag)
    if (value instanceof RationalInterval && value.low.equals(value.high)) {
      // Don't promote if this was explicitly parsed as an interval
      if (value._explicitInterval) {
        return value;
      }

      if (value.low.denominator === 1n) {
        return new Integer(value.low.numerator);
      } else {
        return value.low; // Return as Rational
      }
    }

    // If it's a Rational with denominator 1, convert to Integer
    // BUT only if it wasn't explicitly written as a fraction (has explicitFraction flag)
    if (value instanceof Rational && value.denominator === 1n) {
      // Don't promote if this was explicitly written as a fraction
      if (value._explicitFraction) {
        return value;
      }
      return new Integer(value.numerator);
    }

    return value;
  }

  /**
   * Parses E notation and applies it to the given value
   * Uses base 10 for traditional E notation (when not in base-aware mode)
   * @private
   */
  static #parseENotation(value, expr, options = {}) {
    // Check if we should use base-aware E notation
    if (options.inputBase && options.inputBase !== BaseSystem.DECIMAL) {
      return Parser.#parseBaseAwareENotation(value, expr, options);
    }

    // expr should start with 'E' or 'TE'
    let spaceBeforeE = false;
    let startIndex = 1;

    if (expr.startsWith("TE")) {
      spaceBeforeE = true;
      startIndex = 2;
    } else if (expr[0] === "E") {
      spaceBeforeE = false;
      startIndex = 1;
    } else if (expr.startsWith("_^")) {
      spaceBeforeE = false;
      startIndex = 2;
    } else {
      throw new Error("Expected E notation");
    }

    // Parse the exponent after E
    const exponentResult = Parser.#parseExponent(expr.substring(startIndex));
    const exponent = exponentResult.value;

    // Apply E notation using the value's E method if available, or multiply by power of 10
    let result;
    if (value.E && typeof value.E === "function") {
      result = value.E(exponent);
    } else {
      // Create 10^exponent as a rational (always base 10 for standard E notation)
      let powerOf10;
      if (exponent >= 0n) {
        // Positive exponent: 10^n
        powerOf10 = new Rational(10n ** exponent);
      } else {
        // Negative exponent: 1/(10^(-n))
        powerOf10 = new Rational(1n, 10n ** -exponent);
      }

      // Multiply the value by 10^exponent
      result = value.multiply(powerOf10);
    }

    return {
      value: Parser.#promoteType(result, options),
      remainingExpr: exponentResult.remainingExpr,
    };
  }

  /**
   * Parses base-aware E notation (or _^ notation) and applies it to the given value
   * Uses the input base for both mantissa and exponent parsing
   * @private
   */
  static #parseBaseAwareENotation(value, expr, options = {}) {
    const baseSystem = options.inputBase;
    if (!baseSystem) {
      throw new Error("Base-aware E notation requires inputBase option");
    }

    let notationType;
    let startIndex;

    // Strict E notation and _^ handling
    if (expr.startsWith("_^")) {
      notationType = "_^";
      startIndex = 2;
    } else if (baseSystem.base === 10 && (expr.startsWith("E") || expr.startsWith("e"))) {
      notationType = "E";
      startIndex = 1;
    } else {
      if (baseSystem.base === 10) {
        throw new Error("Expected E or _^ notation");
      } else {
        throw new Error("Scientific notation in non-decimal bases requires _^ separator (e.g. 5_^2)");
      }
    }

    // Extract exponent string
    let endIndex = startIndex;

    // Handle negative exponent
    if (endIndex < expr.length && expr[endIndex] === "-") {
      endIndex++;
    }

    // Parse exponent digits in the input base
    while (endIndex < expr.length) {
      const char = expr[endIndex];
      if (baseSystem.charMap.has(char)) {
        endIndex++;
      } else {
        break;
      }
    }

    if (
      endIndex === startIndex ||
      (endIndex === startIndex + 1 && expr[startIndex] === "-")
    ) {
      throw new Error(`Missing exponent after ${notationType} notation`);
    }

    const exponentStr = expr.substring(startIndex, endIndex);

    // Validate exponent contains only valid base characters
    const testExponentStr = exponentStr.startsWith("-")
      ? exponentStr.substring(1)
      : exponentStr;
    if (!baseSystem.isValidString(testExponentStr)) {
      throw new Error(
        `Invalid exponent "${exponentStr}" for base ${baseSystem.base}`,
      );
    }

    // Parse exponent in the input base
    let exponentDecimal;
    try {
      exponentDecimal = baseSystem.toDecimal(exponentStr);
    } catch (error) {
      throw new Error(
        `Failed to parse exponent "${exponentStr}": ${error.message}`,
      );
    }

    // Apply base-aware E notation: multiply by inputBase^exponent
    let powerOfBase;
    const baseBigInt = BigInt(baseSystem.base);

    if (exponentDecimal >= 0n) {
      // Positive exponent: base^n
      powerOfBase = new Rational(baseBigInt ** exponentDecimal);
    } else {
      // Negative exponent: 1/(base^(-n))
      powerOfBase = new Rational(1n, baseBigInt ** -exponentDecimal);
    }

    // Convert value to Rational for multiplication
    let valueRational;
    if (value instanceof Integer) {
      valueRational = value.toRational();
    } else if (value instanceof Rational) {
      valueRational = value;
    } else {
      throw new Error(
        `${notationType} notation can only be applied to simple numbers, not intervals`,
      );
    }

    const result = valueRational.multiply(powerOfBase);

    return {
      value: Parser.#promoteType(result, options),
      remainingExpr: expr.substring(endIndex),
    };
  }

  /**
   * Parses an interval of the form "a:b" or a single rational number
   * @private
   */
  static #parseInterval(expr, options = {}) {
    // Check if this is uncertainty notation first
    if (
      expr.includes("[") &&
      expr.includes("]") &&
      /^-?[@\w./:^]+\[/.test(expr)
    ) {
      try {
        const result = parseDecimalUncertainty(expr, options);
        return {
          value: result,
          remainingExpr: "",
        };
      } catch {
        // Fall through to other parsing methods
      }
    }

    // Check if this is continued fraction notation (contains .~)
    if (expr.includes('.~')) {
      // Check if this is a continued fraction interval (like 3.~7:22/7 or 1/3:0.~3)
      if (expr.includes(':')) {
        const colonIndex = expr.indexOf(':');
        const leftPart = expr.substring(0, colonIndex);
        const rightPart = expr.substring(colonIndex + 1);

        // Try CF interval parsing if either part contains .~
        if (leftPart.includes('.~') || rightPart.includes('.~')) {
          try {
            // Parse left side (could be CF or regular)
            let leftResult;
            if (leftPart.includes('.~')) {
              leftResult = Parser.#parseContinuedFraction(leftPart, options);
            } else {
              leftResult = Parser.#parseInterval(leftPart, options);
            }

            // Parse right side (could be CF or regular)
            let rightResult;
            if (rightPart.includes('.~')) {
              rightResult = Parser.#parseContinuedFraction(rightPart, options);
            } else {
              rightResult = Parser.#parseInterval(rightPart, options);
            }

            // Convert both to rationals for interval creation
            let leftRational, rightRational;
            if (leftResult.value instanceof Integer) {
              leftRational = leftResult.value.toRational();
            } else if (leftResult.value instanceof Rational) {
              leftRational = leftResult.value;
            } else {
              throw new Error("Left side must evaluate to a rational");
            }

            if (rightResult.value instanceof Integer) {
              rightRational = rightResult.value.toRational();
            } else if (rightResult.value instanceof Rational) {
              rightRational = rightResult.value;
            } else if (rightResult.value instanceof RationalInterval && rightResult.value.isPoint()) {
              rightRational = rightResult.value.low;
            } else {
              throw new Error("Right side must evaluate to a rational");
            }

            // Create the interval
            const interval = new RationalInterval(leftRational, rightRational);
            return {
              value: interval,
              remainingExpr: rightResult.remainingExpr,
            };
          } catch (error) {
            // Fall through to other parsing methods if CF interval parsing fails
          }
        }
      }

      // Try regular continued fraction parsing
      try {
        const cfResult = Parser.#parseContinuedFraction(expr, options);
        return cfResult;
      } catch (error) {
        // Fall through to other parsing methods if CF parsing fails
      }
    }

    // Check if this is a simple decimal (no # and no :)
    if (
      expr.includes(".") &&
      !expr.includes("#") &&
      !expr.includes(":") &&
      !expr.includes("[") &&
      (!options.inputBase || options.inputBase === BaseSystem.DECIMAL)
    ) {
      // Find the end of the decimal number
      let endIndex = 0;
      let hasDecimalPoint = false;

      // Handle optional negative sign
      if (expr[endIndex] === "-") {
        endIndex++;
      }

      // Parse digits and decimal point - use input base if provided
      const baseSystem = options.inputBase || BaseSystem.DECIMAL;
      while (endIndex < expr.length) {
        const char = expr[endIndex];
        if (baseSystem.charMap.has(char)) {
          endIndex++;
        } else if (
          char === "." &&
          !hasDecimalPoint &&
          endIndex + 1 < expr.length &&
          expr[endIndex + 1] !== "."
        ) {
          hasDecimalPoint = true;
          endIndex++;
        } else {
          break;
        }
      }

      if (hasDecimalPoint && endIndex > (expr[0] === "-" ? 2 : 1)) {
        const decimalStr = expr.substring(0, endIndex);
        try {
          if (options.inputBase && options.inputBase !== BaseSystem.DECIMAL) {
            // Parse using input base
            const result = parseBaseNotation(
              decimalStr,
              options.inputBase,
              options,
            );
            return {
              value: result,
              remainingExpr: expr.substring(endIndex),
            };
          } else if (options.typeAware) {
            // Parse as exact rational using Rational constructor for type-aware parsing
            const result = new Rational(decimalStr);
            return {
              value: result,
              remainingExpr: expr.substring(endIndex),
            };
          } else {
            // Use parseNonRepeatingDecimal for backward compatibility (uncertainty intervals)
            const isNegative = decimalStr.startsWith("-");
            const absDecimalStr = isNegative
              ? decimalStr.substring(1)
              : decimalStr;
            const result = parseNonRepeatingDecimal(absDecimalStr, isNegative);
            return {
              value: result,
              remainingExpr: expr.substring(endIndex),
            };
          }
        } catch (error) {
          // Fall through to regular parsing if decimal parsing fails
        }
      }
    }

    // Check if this might be a repeating decimal interval first
    // Only try repeating decimal parsing if the string starts with a digit or decimal point
    // and contains both # and : symbols, indicating it's likely a repeating decimal interval
    if (expr.includes("#") && expr.includes(":") && /^-?[\d.]/.test(expr)) {
      // Find the colon position - need to be careful not to confuse with negative signs
      const colonIndex = expr.indexOf(":");
      if (colonIndex > 0) {
        // Check if the part before the colon looks like a repeating decimal (contains # or just digits/decimal)
        const beforeColon = expr.substring(0, colonIndex);
        const afterColonStart = expr.substring(colonIndex + 1);

        // Only try repeating decimal parsing if both parts look like they could be repeating decimals
        // (contain only digits, decimal points, # symbols, and optional minus sign)
        // AND at least one part actually contains a # symbol for repeating decimals
        if (
          /^-?[\d.#]+$/.test(beforeColon) &&
          /^-?[\d.#]/.test(afterColonStart) &&
          (beforeColon.includes('#') || afterColonStart.includes('#'))
        ) {
          try {
            // Try to parse as repeating decimal interval
            const possibleInterval = parseRepeatingDecimal(expr);
            if (possibleInterval instanceof RationalInterval) {
              // Find how much of the expression this consumed
              let endIndex = expr.length;
              for (let i = 1; i < expr.length; i++) {
                const testExpr = expr.substring(0, i);
                try {
                  const testResult = parseRepeatingDecimal(testExpr);
                  if (testResult instanceof RationalInterval) {
                    // Check if this is followed by a non-digit character or end
                    if (i === expr.length || !/[\d#.\-]/.test(expr[i])) {
                      endIndex = i;
                      const finalResult = parseRepeatingDecimal(
                        expr.substring(0, endIndex),
                      );
                      if (finalResult instanceof RationalInterval) {
                        return {
                          value: finalResult,
                          remainingExpr: expr.substring(endIndex),
                        };
                      }
                    }
                  }
                } catch {
                  // Continue searching
                }
              }

              // Try parsing the whole expression as interval
              try {
                const result = parseRepeatingDecimal(expr);
                if (result instanceof RationalInterval) {
                  return {
                    value: result,
                    remainingExpr: "",
                  };
                }
              } catch {
                // Fall through to regular parsing
              }
            }
          } catch {
            // Fall through to regular rational parsing
          }
        }
      }
    }

    // Check for input base parsing first if specified
    const debugMatch = expr.trim().match(/^(-?)0[a-zA-Z]/);
    // console.log(`Parser Debug: expr='${expr}', match=${!!debugMatch}, inputBase=${options.inputBase?.name}`);

    if (
      options.inputBase &&
      options.inputBase !== BaseSystem.DECIMAL &&
      !expr.includes("[") &&
      !expr.includes("#") &&
      !debugMatch
    ) {
      // Try to parse the entire expression with input base first
      try {
        // Find the end of what could be a number in the input base
        let endIndex = 0;
        let hasDecimalPoint = false;
        let hasMixedNumber = false;
        let hasFraction = false;
        let hasColon = false;

        // Handle negative sign
        if (expr[endIndex] === "-") {
          endIndex++;
        }

        // Parse the number pattern
        while (endIndex < expr.length) {
          const char = expr[endIndex];

          if (options.inputBase.charMap.has(char)) {
            endIndex++;
          } else if (/[0-9]/.test(char)) {
            // Greedy digit consumption
            endIndex++;
          } else if (
            char === "." &&
            endIndex + 1 < expr.length &&
            expr[endIndex + 1] === "."
          ) {
            // Mixed number notation
            if (hasMixedNumber || hasDecimalPoint || hasFraction || hasColon)
              break;
            hasMixedNumber = true;
            endIndex += 2;
          } else if (char === "." && !hasDecimalPoint && !hasMixedNumber) {
            // Decimal point
            hasDecimalPoint = true;
            endIndex++;
          } else if (char === "/" && !hasFraction) {
            // Fraction
            hasFraction = true;
            endIndex++;
          } else if (
            char === ":" &&
            !hasColon &&
            !hasMixedNumber
          ) {
            // Interval notation
            hasColon = true;
            hasDecimalPoint = false;
            hasFraction = false;
            endIndex++;
          } else {
            break;
          }
        }

        // If we found a valid pattern, try parsing with input base
        if (endIndex > (expr[0] === "-" ? 1 : 0)) {
          const numberStr = expr.substring(0, endIndex);

          // Validate that all parts are valid in the input base
          const testStr = numberStr.startsWith("-")
            ? numberStr.substring(1)
            : numberStr;
          const parts = testStr.split(/[\.\/\:]/);
          const isValidInBase = parts.every((part, index) => {
            if (part === "") {
              return (
                testStr.includes(".") &&
                (index === 0 || index === parts.length - 1)
              );
            }
            return part
              .split("")
              .every((char) => options.inputBase.charMap.has(char));
          });

          if (isValidInBase) {
            const result = parseBaseNotation(
              numberStr,
              options.inputBase,
              options,
            );
            return {
              value: result,
              remainingExpr: expr.substring(endIndex),
            };
          } else if (options.inputBase && options.inputBase !== BaseSystem.DECIMAL) {
            throw new Error(`Invalid number format for ${options.inputBase.name}`);
          }
        } else if (options.inputBase && options.inputBase !== BaseSystem.DECIMAL) {
          const firstChar = expr.startsWith("-") ? expr[1] : expr[0];
          if (/[0-9]/.test(firstChar)) {
            throw new Error(`Invalid number format for ${options.inputBase.name}`);
          }
        }
      } catch (error) {
        // Fall through to regular parsing
      }
    }


    // Parse the first rational number
    const firstResult = Parser.#parseRational(expr, options);

    // Check if there's E notation followed by a colon (like "3E1:4E1")
    let firstValue = firstResult.value;
    let remainingAfterFirst = firstResult.remainingExpr;

    // Look for E notation before checking for colon
    if (remainingAfterFirst.length > 0 && remainingAfterFirst[0] === "E") {
      // Find where the E notation ends by looking for non-digit characters after E
      let eEndIndex = 1; // Start after the 'E'
      if (
        eEndIndex < remainingAfterFirst.length &&
        remainingAfterFirst[eEndIndex] === "-"
      ) {
        eEndIndex++; // Skip negative sign
      }
      while (
        eEndIndex < remainingAfterFirst.length &&
        /\d/.test(remainingAfterFirst[eEndIndex])
      ) {
        eEndIndex++;
      }

      // If there's a colon after the E notation, this is an interval like "3E1:4E1"
      if (
        eEndIndex < remainingAfterFirst.length &&
        remainingAfterFirst[eEndIndex] === ":"
      ) {
        // Apply E notation to the first value
        const eNotationPart = remainingAfterFirst.substring(0, eEndIndex);
        const firstInterval = RationalInterval.point(firstResult.value);
        const eResult = Parser.#parseENotation(
          firstInterval,
          eNotationPart,
          options,
        );

        // Handle type-promoted result - extract the rational value
        if (eResult.value instanceof RationalInterval) {
          firstValue = eResult.value.low;
        } else if (eResult.value instanceof Rational) {
          firstValue = eResult.value;
        } else if (eResult.value instanceof Integer) {
          firstValue = eResult.value.toRational();
        } else {
          firstValue = eResult.value;
        }

        remainingAfterFirst = remainingAfterFirst.substring(eEndIndex);
      }
    }

    // If no colon follows, return the appropriate type based on parsing mode
    if (remainingAfterFirst.length === 0 || remainingAfterFirst[0] !== ":") {
      if (options.typeAware) {
        // Type-aware parsing: return Integer for whole numbers, Rational otherwise
        if (firstValue instanceof Rational && firstValue.denominator === 1n) {
          // Don't promote if this was explicitly written as a fraction
          if (firstValue._explicitFraction) {
            return {
              value: firstValue,
              remainingExpr: remainingAfterFirst,
            };
          }
          // This is a whole number, return as Integer
          return {
            value: new Integer(firstValue.numerator),
            remainingExpr: remainingAfterFirst,
          };
        }
        // Return as the original type (Rational)
        return {
          value: firstValue,
          remainingExpr: remainingAfterFirst,
        };
      } else {
        // Backward compatible parsing: always return point intervals
        const pointValue = RationalInterval.point(firstValue);
        return {
          value: pointValue,
          remainingExpr: remainingAfterFirst,
        };
      }
    }

    // Parse the second rational after the colon
    const secondRationalExpr = remainingAfterFirst.substring(1);
    const secondResult = Parser.#parseRational(secondRationalExpr, options);

    // Check if the second part has tight E notation
    let secondValue = secondResult.value;
    let remainingExpr = secondResult.remainingExpr;

    if (
      remainingExpr.length > 0 &&
      (remainingExpr[0] === "E" || remainingExpr.startsWith("_^"))
    ) {
      // Apply E notation to the second value only (only for tight binding, not spaced)
      const secondInterval = RationalInterval.point(secondResult.value);
      const eResult = Parser.#parseENotation(
        secondInterval,
        remainingExpr,
        options,
      );

      // Handle type-promoted result - extract the rational value
      if (eResult.value instanceof RationalInterval) {
        secondValue = eResult.value.low;
      } else if (eResult.value instanceof Rational) {
        secondValue = eResult.value;
      } else if (eResult.value instanceof Integer) {
        secondValue = eResult.value.toRational();
      } else {
        secondValue = eResult.value;
      }

      remainingExpr = eResult.remainingExpr;
    }

    // Mark this as an explicit interval to prevent type promotion
    const interval = new RationalInterval(firstValue, secondValue);
    interval._explicitInterval = true;

    return {
      value: interval,
      remainingExpr: remainingExpr,
    };
  }

  /**
   * Parses a rational number of the form "a/b", "a", mixed number "a..b/c", or repeating decimal "a.b#c"
   * Supports input base parsing when options.inputBase is provided
   * @private
   */
  static #parseRational(expr, options = {}) {
    expr = expr.trim();
    // Check for prefix notation at the start (e.g., 0z[N], 0x, 0b) and override input base
    const prefixMatch = expr.trim().match(/^(-?)(?:0z\[(\d+)\]|0([a-zA-Z]))/i);
    let isExplicitPrefix = false;

    if (prefixMatch) {
      const isNegative = prefixMatch[1] === "-";
      let baseSystem;
      let matchedPrefixLength;

      if (prefixMatch[2]) { // Matched 0z[N]
        try {
          baseSystem = BaseSystem.fromBase(parseInt(prefixMatch[2], 10));
          matchedPrefixLength = prefixMatch[0].length;
        } catch (e) {
          throw new Error(`Invalid custom base '0z[${prefixMatch[2]}]': ${e.message}`);
        }
      } else if (prefixMatch[3]) { // Matched 0[letter]
        const prefixChar = prefixMatch[3];
        baseSystem = BaseSystem.getSystemForPrefix(prefixChar);
        matchedPrefixLength = prefixMatch[0].length;

        if (!baseSystem && prefixChar.toLowerCase() !== "e" && prefixChar !== "D") {
          // Strictly require valid prefixes for 0[letter] notation, unless 'E' or 'D'
          throw new Error(`Invalid or unregistered prefix '0${prefixChar}'`);
        }
      }

      if (baseSystem || (prefixMatch[3] && prefixMatch[3] === "D")) { // 'D' is a special case for default base
        // Create new options with the specific base
        options = { ...options, inputBase: baseSystem || options.inputBase };
        isExplicitPrefix = true;

        // Strip the prefix (keep negative sign if present)
        expr = (isNegative ? "-" : "") + expr.substring(matchedPrefixLength);
      }
    } if (expr.length === 0) {
      throw new Error("Unexpected end of expression");
    }

    // If inputBase is specified and this doesn't look like explicit base notation (old bracket style)
    // or hash/positional notation for the CURRENT number, try parsing with the input base first
    if (
      options.inputBase &&
      options.inputBase !== BaseSystem.DECIMAL &&
      !/^[\w./:^-]+\[\d+\]/.test(expr) &&
      !/^[\w./:^-]+#/.test(expr)
    ) {
      // Find the end of what could be a number in the input base
      let endIndex = 0;
      let hasDecimalPoint = false;
      let hasMixedNumber = false;
      let hasFraction = false;
      let hasExponent = false;
      let validationBase = options.inputBase;

      // Handle negative sign
      if (expr[endIndex] === "-") {
        endIndex++;
      }

      // Parse the number pattern
      while (endIndex < expr.length) {
        const char = expr[endIndex];

        // Check if character is valid in base (with case normalization)
        let isValidChar = validationBase.charMap.has(char);

        // Handle case normalization for bases with letters
        if (!isValidChar) {
          const baseUsesLowercase = validationBase.characters.some(
            (ch) => ch >= "a" && ch <= "z",
          );
          const baseUsesUppercase = validationBase.characters.some(
            (ch) => ch >= "A" && ch <= "Z",
          );

          // If base uses only lowercase letters, accept uppercase input
          if (
            baseUsesLowercase &&
            !baseUsesUppercase &&
            char >= "A" &&
            char <= "Z"
          ) {
            isValidChar = validationBase.charMap.has(char.toLowerCase());
          }
          // If base uses only uppercase letters, accept lowercase input
          else if (
            baseUsesUppercase &&
            !baseUsesLowercase &&
            char >= "a" &&
            char <= "z"
          ) {
            isValidChar = validationBase.charMap.has(char.toUpperCase());
          }
        }

        if (isValidChar) {
          endIndex++;
        } else if (
          char === "." &&
          endIndex + 1 < expr.length &&
          expr[endIndex + 1] === "."
        ) {
          // Mixed number notation
          if (hasMixedNumber || hasDecimalPoint || hasFraction) break;
          hasMixedNumber = true;
          endIndex += 2;
        } else if (char === "." && !hasDecimalPoint && !hasMixedNumber) {
          // Decimal point
          hasDecimalPoint = true;
          endIndex++;
        } else if (char === "/" && !hasFraction) {
          // Fraction
          // Check for division operator ambiguity (e.g. 0xFF / (...))
          // If next char is likely not part of the number/fraction, break.
          // This handles space, '(', and other operators.
          if (endIndex + 1 < expr.length) {
            const nextChar = expr[endIndex + 1];
            if (!validationBase.charMap.has(nextChar)) {
              break;
            }
          }

          hasFraction = true;
          endIndex++;

          // Check for prefix syntax in denominator (e.g. 1/0b10)
          // If found, switch validation base for the denominator part
          // Also check for 0z[N]
          if (endIndex < expr.length) { // Check if there's anything left to parse
            const potentialPrefix = expr.substring(endIndex); // Check rest of string
            const customSubMatch = potentialPrefix.match(/^0z\[(\d+)\]/i);
            const subPrefixMatch = potentialPrefix.match(/^0([a-zA-Z])/);

            if (customSubMatch) {
              try {
                validationBase = BaseSystem.fromBase(parseInt(customSubMatch[1], 10));
                endIndex += customSubMatch[0].length;
              } catch (e) {
                // If invalid custom base, treat it as not a prefix and continue
                // The number parsing will fail later if it's not a valid number in the current base
              }
            } else if (subPrefixMatch) {
              const prefixChar = subPrefixMatch[1];
              const subBase = BaseSystem.getSystemForPrefix(prefixChar);
              if (subBase || prefixChar === "D") {
                if (subBase) validationBase = subBase;
                endIndex += 2;
              }
            }
          }
        } else if (validationBase.characters.includes(char.toUpperCase()) && (char === "E" || char === "e")) {
          // If 'E' or 'e' is a valid digit in the current base (e.g., Hexadecimal)
          endIndex++;
        } else if (
          (char === "E" && !options.disableENotation) ||
          (char === "_" && endIndex + 1 < expr.length && expr[endIndex + 1] === "^")
        ) {
          // E-notation or Base-aware scientific notation (_^)
          // Allowed if strict E-notation enabled (removed 'e' check) or explicit prefix uses _^
          hasExponent = true;
          endIndex += (char === "_") ? 2 : 1;

          // Consume optional sign
          if (endIndex < expr.length && (expr[endIndex] === "+" || expr[endIndex] === "-")) {
            endIndex++;
          }
          // Continue to consume exponent digits loop
        } else if (/[0-9]/.test(char)) {
          // Consume any digit 0-9 even if not in base to avoid partial matches
          // Validation will catch this later
          endIndex++;
        } else {
          // Invalid character for this base
          break;
        }
      }

      // If we found a valid number pattern and it looks like a valid base number, try parsing
      // console.log(`loop finished. endIndex=${endIndex} exprLen=${expr.length} explicit=${isExplicitPrefix}`);

      // If explicit prefix used but no valid digits found, throw Error immediately
      if (isExplicitPrefix && endIndex <= (expr[0] === "-" ? 1 : 0)) {
        throw new Error(`Invalid number format for ${options.inputBase.name}`);
      }

      if (endIndex > (expr[0] === "-" ? 1 : 0)) {
        const numberStr = expr.substring(0, endIndex);

        // Check if this looks like a valid number in the input base
        const testStr = numberStr.startsWith("-")
          ? numberStr.substring(1)
          : numberStr;

        // Split on decimal point and slash, but handle empty parts correctly
        // For mixed numbers (12..101/211), we need special handling since ".." creates empty parts
        // Split on decimal point and slash, but handle empty parts correctly
        // For mixed numbers (12..101/211), we need special handling since ".." creates empty parts
        const parts = testStr.split(/[\.\/]/);

        let isValidInBase = true;
        if (!isExplicitPrefix) {
          isValidInBase = parts.every((part, index) => {
            // Allow empty parts for decimal points (e.g., ".5" or "5.")
            // and for mixed numbers (e.g., "12..101" becomes ["12", "", "101"])
            if (part === "") {
              return (
                testStr.includes(".") &&
                (index === 0 ||
                  index === parts.length - 1 ||
                  testStr.includes(".."))
              );
            }
            // Check if all characters in this part are valid for the base
            // Handle case normalization for bases with letters
            const baseUsesLowercase = options.inputBase.characters.some(
              (char) => char >= "a" && char <= "z",
            );
            const baseUsesUppercase = options.inputBase.characters.some(
              (char) => char >= "A" && char <= "Z",
            );

            return part.split("").every((char) => {
              // Check direct match first
              if (options.inputBase.charMap.has(char)) {
                return true;
              }

              // If base uses only lowercase letters, check uppercase input
              if (
                baseUsesLowercase &&
                !baseUsesUppercase &&
                char >= "A" &&
                char <= "Z"
              ) {
                return options.inputBase.charMap.has(char.toLowerCase());
              }

              // If base uses only uppercase letters, check lowercase input
              if (
                baseUsesUppercase &&
                !baseUsesLowercase &&
                char >= "a" &&
                char <= "z"
              ) {
                return options.inputBase.charMap.has(char.toUpperCase());
              }

              return false;
            });
          });
        }

        if (isValidInBase) {
          try {
            const result = parseBaseNotation(
              numberStr,
              options.inputBase,
              options,
            );
            return {
              value: result,
              remainingExpr: expr.substring(endIndex),
            };
          } catch (error) {
            // If explicit base was used, rethrow the error (don't fallback)
            if (isExplicitPrefix) {
              throw error;
            }
            // If base parsing fails for implicit base, fall through to decimal parsing
          }
        } else if (options.inputBase && options.inputBase !== BaseSystem.DECIMAL) {
          // If we consumed a number-like sequence that is invalid for the input base, 
          // throw an error instead of falling through to decimal
          throw new Error(`Invalid number format for ${options.inputBase.name}`);
        }
      } else if (options.inputBase && options.inputBase !== BaseSystem.DECIMAL) {
        // Handle case where we didn't consume anything but it looks like a number start (digit)
        const firstChar = expr.startsWith("-") ? expr[1] : expr[0];
        if (/[0-9]/.test(firstChar)) {
          throw new Error(`Invalid number format for ${options.inputBase.name}`);
        }
      }
    }

    // Check for repeating decimal notation first
    let hashIndex = expr.indexOf("#");
    if (hashIndex !== -1) {
      // Only try repeating decimal parsing if the part before # looks like a decimal number
      // (contains only digits, decimal point, and optional minus sign)
      const beforeHash = expr.substring(0, hashIndex);
      if (/^-?(\d+\.?\d*|\.\d+)$/.test(beforeHash)) {
        // Find the end of the repeating decimal
        let endIndex = hashIndex + 1;
        while (endIndex < expr.length && /\d/.test(expr[endIndex])) {
          endIndex++;
        }

        const repeatingDecimalStr = expr.substring(0, endIndex);
        try {
          const result = parseRepeatingDecimal(repeatingDecimalStr);

          // If result is an interval, treat it as a point interval for the rational
          if (result instanceof RationalInterval) {
            // For parsing in expressions, use the midpoint of the interval
            const midpoint = result.low
              .add(result.high)
              .divide(new Rational(2));
            return {
              value: midpoint,
              remainingExpr: expr.substring(endIndex),
            };
          } else {
            return {
              value: result,
              remainingExpr: expr.substring(endIndex),
            };
          }
        } catch (error) {
          throw new Error(`Invalid repeating decimal: ${error.message}`);
        }
      }
    }

    // Check for regular decimal notation (contains single decimal point but no # and not mixed number ..)
    let decimalIndex = expr.indexOf(".");
    if (
      decimalIndex !== -1 &&
      decimalIndex + 1 < expr.length &&
      expr[decimalIndex + 1] !== "."
    ) {
      // Find the end of the decimal number
      let endIndex = 0;
      let hasDecimalPoint = false;

      // Handle optional negative sign
      if (expr[endIndex] === "-") {
        endIndex++;
      }

      // Parse digits and decimal point
      while (endIndex < expr.length) {
        if (/\d/.test(expr[endIndex])) {
          endIndex++;
        } else if (
          expr[endIndex] === "." &&
          !hasDecimalPoint &&
          endIndex + 1 < expr.length &&
          expr[endIndex + 1] !== "."
        ) {
          hasDecimalPoint = true;
          endIndex++;
        } else {
          break;
        }
      }

      if (hasDecimalPoint && endIndex > (expr[0] === "-" ? 2 : 1)) {
        const decimalStr = expr.substring(0, endIndex);
        try {
          const result = new Rational(decimalStr);
          return {
            value: result,
            remainingExpr: expr.substring(endIndex),
          };
        } catch (error) {
          // Fall through to regular parsing if decimal parsing fails
        }
      }
    }

    let i = 0;
    let numeratorStr = "";
    let denominatorStr = "";
    let isNegative = false;
    let wholePart = 0n;
    let hasMixedForm = false;

    // Handle negative sign
    if (expr[i] === "-") {
      isNegative = true;
      i++;
    }

    // Parse whole number part or numerator
    while (i < expr.length && /\d/.test(expr[i])) {
      numeratorStr += expr[i];
      i++;
    }

    if (numeratorStr.length === 0) {
      throw new Error("Invalid rational number format");
    }

    // Check for mixed number notation (double dot)
    if (i + 1 < expr.length && expr[i] === "." && expr[i + 1] === ".") {
      hasMixedForm = true;
      wholePart = isNegative ? -BigInt(numeratorStr) : BigInt(numeratorStr);
      isNegative = false; // Sign already applied to whole part
      i += 2; // Skip past the '..'

      // Reset for fraction part
      numeratorStr = "";

      // Parse numerator of fractional part
      while (i < expr.length && /\d/.test(expr[i])) {
        numeratorStr += expr[i];
        i++;
      }

      if (numeratorStr.length === 0) {
        throw new Error(
          'Invalid mixed number format: missing numerator after ".."',
        );
      }
    }

    // Track whether this was explicitly written as a fraction
    let explicitFraction = false;

    // Check for denominator
    if (i < expr.length && expr[i] === "/") {
      explicitFraction = true;
      i++;

      // Check for space marker after '/' - if present, treat as division operation
      if (i < expr.length && expr[i] === "S") {
        // There was whitespace after '/', so this should be division, not a fraction
        if (hasMixedForm) {
          throw new Error("Invalid mixed number format: missing denominator");
        }
        // Return just the numerator as a rational and let division be handled at term level
        const numerator = isNegative
          ? -BigInt(numeratorStr)
          : BigInt(numeratorStr);
        return {
          value: new Rational(numerator, 1n),
          remainingExpr: expr.substring(i - 1), // Include the '/' in remaining (skip the 'S')
        };
      }

      // Check if what follows is a simple numeric denominator or something complex
      if (i < expr.length && expr[i] === "(") {
        // Complex denominator starting with parentheses - don't try to parse here
        // Return what we have so far and let higher-level parsing handle the division
        if (hasMixedForm) {
          throw new Error("Invalid mixed number format: missing denominator");
        }
        // Return just the numerator as a rational and let division be handled at term level
        const numerator = isNegative
          ? -BigInt(numeratorStr)
          : BigInt(numeratorStr);
        return {
          value: new Rational(numerator, 1n),
          remainingExpr: expr.substring(i - 1), // Include the '/' in remaining
        };
      }

      while (i < expr.length && /\d/.test(expr[i])) {
        denominatorStr += expr[i];
        i++;
      }

      if (denominatorStr.length === 0) {
        throw new Error("Invalid rational number format");
      }

      // Check if E follows immediately after fraction (invalid)
      if (i < expr.length && expr[i] === "E") {
        throw new Error(
          "E notation not allowed directly after fraction without parentheses",
        );
      }
    } else {
      // If no denominator specified
      if (hasMixedForm) {
        throw new Error("Invalid mixed number format: missing denominator");
      }
      denominatorStr = "1";
    }

    // Check if E follows immediately after mixed number (invalid)
    if (hasMixedForm && i < expr.length && expr[i] === "E") {
      throw new Error(
        "E notation not allowed directly after mixed number without parentheses",
      );
    }

    // Create the rational number
    let numerator, denominator;

    if (hasMixedForm) {
      // Convert mixed number to improper fraction
      numerator = BigInt(numeratorStr);
      denominator = BigInt(denominatorStr);

      // Calculate: whole + numerator/denominator
      // => (whole * denominator + numerator) / denominator
      const sign = wholePart < 0n ? -1n : 1n;
      numerator =
        sign *
        ((wholePart.valueOf() < 0n ? -wholePart : wholePart) * denominator +
          numerator);
    } else {
      numerator = isNegative ? -BigInt(numeratorStr) : BigInt(numeratorStr);
      denominator = BigInt(denominatorStr);
    }

    // Handle division by zero within the parse step
    if (denominator === 0n) {
      throw new Error("Denominator cannot be zero");
    }

    const rational = new Rational(numerator, denominator);

    // Mark as explicit fraction if it was written with / and denominator is 1
    if (explicitFraction && denominator === 1n) {
      rational._explicitFraction = true;
    }

    return {
      value: rational,
      remainingExpr: expr.substring(i),
    };
  }

  /**
   * Parses continued fraction notation like 3.~7~15~1~292
   * @private
   */
  static #parseContinuedFraction(expr, options = {}) {
    // Match continued fraction pattern: integer.~term1~term2~...
    const cfMatch = expr.match(/^(-?\d+)\.~((?:\d+~?)*\d*)(.*)$/);
    if (!cfMatch) {
      throw new Error("Invalid continued fraction format");
    }

    const [fullMatch, integerPart, cfTermsStr, remaining] = cfMatch;

    // Validate format
    if (cfTermsStr === '') {
      throw new Error("Continued fraction must have at least one term after .~");
    }

    // Handle trailing tilde validation
    if (cfTermsStr.endsWith('~')) {
      throw new Error("Continued fraction cannot end with ~");
    }

    // Handle double tildes
    if (cfTermsStr.includes('~~')) {
      throw new Error("Invalid continued fraction format: double tilde");
    }

    // Parse the coefficient array
    const cfArray = Parser.parseContinuedFraction(fullMatch.substring(0, fullMatch.length - remaining.length));

    // Convert to Rational using the forthcoming fromContinuedFraction method
    // For now, we'll create a placeholder - this will be implemented when we add the Rational class method
    if (typeof Rational.fromContinuedFraction === 'function') {
      const rational = Rational.fromContinuedFraction(cfArray);
      return {
        value: rational,
        remainingExpr: remaining
      };
    } else {
      throw new Error("Continued fraction support not yet implemented in Rational class");
    }
  }

  /**
   * Parses a continued fraction string into coefficient array
   * This is the stand-alone parsing that generates array of coefficients
   * @param {string} cfString - String like "3.~7~15~1~292"
   * @returns {Array<bigint>} Array [integer_part, ...continued_fraction_terms]
   */
  static parseContinuedFraction(cfString) {
    // Match the pattern
    const cfMatch = cfString.match(/^(-?\d+)\.~(.*)$/);
    if (!cfMatch) {
      throw new Error("Invalid continued fraction format");
    }

    const [, integerPart, cfTermsStr] = cfMatch;

    // Parse integer part
    const intPart = BigInt(integerPart);

    // Handle special case of integer representation like "5.~0"
    if (cfTermsStr === '0') {
      return [intPart];
    }

    // Validate terms string
    if (cfTermsStr === '') {
      throw new Error("Continued fraction must have at least one term after .~");
    }

    if (cfTermsStr.endsWith('~')) {
      throw new Error("Continued fraction cannot end with ~");
    }

    if (cfTermsStr.includes('~~')) {
      throw new Error("Invalid continued fraction format: double tilde");
    }

    // Split terms and validate they are all positive integers (except the integer part)
    const terms = cfTermsStr.split('~');
    const cfTerms = [];

    for (const term of terms) {
      if (!/^\d+$/.test(term)) {
        throw new Error(`Invalid continued fraction term: ${term}`);
      }
      const termValue = BigInt(term);
      if (termValue <= 0n) {
        throw new Error(`Continued fraction terms must be positive integers: ${term}`);
      }
      cfTerms.push(termValue);
    }

    return [intPart, ...cfTerms];
  }
}

/**
 * Template string function for parsing rational arithmetic expressions
 * Uses the main parser with type-aware parsing (returns Integer, Rational, or RationalInterval)
 * 
 * @param {TemplateStringsArray} strings - Template string parts
 * @param {...any} values - Interpolated values
 * @returns {Integer|Rational|RationalInterval} Parsed result
 */
export function R(strings, ...values) {
  let input = '';
  for (let i = 0; i < values.length; i++) {
    input += strings[i] + values[i];
  }
  input += strings[strings.length - 1];
  return Parser.parse(input, { typeAware: true });
}

/**
 * Template string function for parsing into Fraction and FractionInterval types
 * Forces results to use Fraction/FractionInterval classes regardless of input
 * 
 * @param {TemplateStringsArray} strings - Template string parts
 * @param {...any} values - Interpolated values
 * @returns {Fraction|FractionInterval} Parsed result as Fraction types
 */
export function F(strings, ...values) {
  let input = '';
  for (let i = 0; i < values.length; i++) {
    input += strings[i] + values[i];
  }
  input += strings[strings.length - 1];

  // Parse with type-aware disabled to get intervals when appropriate
  const result = Parser.parse(input, { typeAware: false });

  // Convert result to Fraction types
  if (result instanceof RationalInterval) {
    // Check if this is a point interval (low equals high)
    if (result.low.equals(result.high)) {
      // Convert point interval to single Fraction
      return Fraction.fromRational(result.low);
    } else {
      // Convert RationalInterval to FractionInterval
      const lowFrac = Fraction.fromRational(result.low);
      const highFrac = Fraction.fromRational(result.high);
      return new FractionInterval(lowFrac, highFrac);
    }
  } else if (result instanceof Rational) {
    // Convert Rational to Fraction
    return Fraction.fromRational(result);
  } else if (result instanceof Integer) {
    // Convert Integer to Fraction
    return new Fraction(result.value, 1n);
  } else {
    // Fallback: convert whatever we got to Fraction
    const rational = result.toRational ? result.toRational() : new Rational(result.toString());
    return Fraction.fromRational(rational);
  }
}
